<template>
  <div class="bazi-panel">

    <!-- ==================== 移动端模板 ==================== -->
    <template v-if="isMobile">
      <!-- 移动端：输入区域 -->
      <div class="mobile-input-card">
        <div class="mobile-section-title">出生日期</div>
        <div class="mobile-date-grid">
          <div class="mobile-date-item">
            <el-select v-model="formData.year" @change="calculateResult" placeholder="年" filterable clearable size="small">
              <el-option v-for="year in yearOptions" :key="year.value" :label="year.value + '年'" :value="year.value" />
            </el-select>
            <span class="mobile-date-unit">年</span>
          </div>
          <div class="mobile-date-item">
            <el-select v-model="formData.month" @change="calculateResult" placeholder="月" clearable size="small">
              <el-option v-for="m in 12" :key="m" :label="m + '月'" :value="m" />
            </el-select>
            <span class="mobile-date-unit">月</span>
          </div>
          <div class="mobile-date-item">
            <el-select v-model="formData.day" @change="calculateResult" placeholder="日" clearable size="small">
              <el-option v-for="d in daysInMonth" :key="d" :label="d + '日'" :value="d" />
            </el-select>
            <span class="mobile-date-unit">日</span>
          </div>
          <div class="mobile-date-item">
            <el-select v-model="formData.hour" @change="calculateResult" placeholder="时辰" clearable size="small">
              <el-option label="子时" value="23" />
              <el-option label="丑时" value="1" />
              <el-option label="寅时" value="3" />
              <el-option label="卯时" value="5" />
              <el-option label="辰时" value="7" />
              <el-option label="巳时" value="9" />
              <el-option label="午时" value="11" />
              <el-option label="未时" value="13" />
              <el-option label="申时" value="15" />
              <el-option label="酉时" value="17" />
              <el-option label="戌时" value="19" />
              <el-option label="亥时" value="21" />
            </el-select>
            <span class="mobile-date-unit">时</span>
          </div>
        </div>
        <!-- 性别 segmented control -->
        <div class="mobile-gender-row">
          <button
            class="mobile-gender-btn"
            :class="{ active: formData.gender === '男' }"
            @click="formData.gender = '男'; calculateResult()"
          >♂ 男</button>
          <button
            class="mobile-gender-btn"
            :class="{ active: formData.gender === '女' }"
            @click="formData.gender = '女'; calculateResult()"
          >♀ 女</button>
        </div>
      </div>

      <!-- 移动端：结果区 -->
      <template v-if="result">

        <!-- 移动端：基本信息 -->
        <div class="mobile-card">
          <div class="mobile-section-title">基本信息</div>
          <div class="mobile-info-grid">
            <div class="mobile-info-item">
              <span class="mobile-info-label">公历</span>
              <span class="mobile-info-value">{{ formatDate(new Date(formData.year, formData.month - 1, formData.day)) }}</span>
            </div>
            <div class="mobile-info-item">
              <span class="mobile-info-label">农历</span>
              <span class="mobile-info-value">{{ result.lunarDate || '-' }}</span>
            </div>
            <div class="mobile-info-item">
              <span class="mobile-info-label">生肖</span>
              <span class="mobile-info-value">{{ result.zodiac || '-' }}</span>
            </div>
            <div class="mobile-info-item">
              <span class="mobile-info-label">性别</span>
              <span class="mobile-info-value">{{ formData.gender }}</span>
            </div>
            <div class="mobile-info-item">
              <span class="mobile-info-label">节气</span>
              <span class="mobile-info-value">{{ result.solarTerm?.current?.name || '-' }}</span>
            </div>
            <div class="mobile-info-item">
              <span class="mobile-info-label">季节</span>
              <span class="mobile-info-value">{{ result.solarTerm?.season || '-' }}</span>
            </div>
            <div class="mobile-info-item">
              <span class="mobile-info-label">纳音(日)</span>
              <span class="mobile-info-value">{{ result.naYin?.day || '-' }}</span>
            </div>
            <div class="mobile-info-item">
              <span class="mobile-info-label">建除神</span>
              <span class="mobile-info-value">{{ result.dutyGod?.name || '无' }}</span>
            </div>
          </div>
        </div>

        <!-- 移动端：四柱命盘（纵向卡片堆叠） -->
        <div class="mobile-card">
          <div class="mobile-section-title">四柱命盘</div>
          <div class="mobile-pillar-list">
            <div v-for="pillar in pillarData" :key="pillar.label" class="mobile-pillar-card">
              <div class="mobile-pillar-header">{{ pillar.label }}</div>
              <div class="mobile-pillar-content">
                <div class="mobile-pillar-gan">
                  <span class="mobile-pillar-char" :class="getElementClass(pillar.stem)">{{ pillar.stem || '-' }}</span>
                  <span class="mobile-pillar-attr">{{ ganWuxingMap[pillar.stem] || '' }}</span>
                </div>
                <div class="mobile-pillar-divider"></div>
                <div class="mobile-pillar-zhi">
                  <span class="mobile-pillar-char" :class="getElementClass(pillar.branch)">{{ pillar.branch || '-' }}</span>
                  <span class="mobile-pillar-attr">{{ ganWuxingMap[pillar.branch] || '' }}</span>
                </div>
              </div>
              <div class="mobile-pillar-meta">
                <span v-if="pillar.shiShen" class="mobile-pillar-tag">十神: {{ pillar.shiShen }}</span>
                <span v-if="pillar.naYin" class="mobile-pillar-tag">纳音: {{ pillar.naYin }}</span>
              </div>
              <div class="mobile-pillar-meta">
                <span class="mobile-pillar-tag">藏干: {{ formatCangGanText(pillar.cangGan) }}</span>
              </div>
              <div class="mobile-pillar-meta" v-if="pillar.lifeGod">
                <span class="mobile-pillar-tag">长生: {{ pillar.lifeGod }}</span>
                <span v-if="pillar.kongWang" class="mobile-pillar-tag">空亡: {{ pillar.kongWang }}</span>
              </div>
              <div class="mobile-pillar-relations" v-if="formatRelationsText(pillar.relations).length">
                <el-tag
                  v-for="rel in formatRelationsText(pillar.relations)"
                  :key="rel.text"
                  :type="rel.type"
                  size="small"
                  class="mobile-relation-tag"
                >{{ rel.text }}</el-tag>
              </div>
            </div>
          </div>
        </div>

        <!-- 移动端：五行分布（彩色进度条） -->
        <div class="mobile-card">
          <div class="mobile-section-title">五行分布</div>
          <div class="mobile-wuxing-section">
            <div class="mobile-daymaster">
              日主：{{ result.dayMaster?.element || '-' }}{{ result.dayMaster?.strength > 0 ? '旺' : '弱' }}
              (得分:{{ result.dayMaster?.strength || 0 }})
            </div>
            <div v-for="(count, element) in result.elements" :key="element" class="mobile-wuxing-row">
              <span class="mobile-wuxing-label" :style="{ color: wuxingColors[element] }">{{ element }}</span>
              <div class="mobile-wuxing-bar-bg">
                <div
                  class="mobile-wuxing-bar-fill"
                  :style="{
                    width: (count / wuxingMax * 100) + '%',
                    backgroundColor: wuxingColors[element]
                  }"
                ></div>
              </div>
              <span class="mobile-wuxing-count">{{ count }}</span>
            </div>
          </div>
        </div>

        <!-- 移动端：阴阳统计 -->
        <div class="mobile-card">
          <div class="mobile-section-title">阴阳统计</div>
          <div class="mobile-info-grid">
            <div class="mobile-info-item">
              <span class="mobile-info-label">八字阴阳</span>
              <span class="mobile-info-value">阴{{ result.yinYangCount?.yin || 0 }} / 阳{{ result.yinYangCount?.yang || 0 }}</span>
            </div>
            <div class="mobile-info-item">
              <span class="mobile-info-label">藏干阴阳</span>
              <span class="mobile-info-value">阴{{ result.hiddenYinYangCount?.yin || 0 }} / 阳{{ result.hiddenYinYangCount?.yang || 0 }}</span>
            </div>
          </div>
        </div>

        <!-- 移动端：大运流年 -->
        <div class="mobile-card">
          <div class="mobile-section-title">大运流年</div>
          <div class="mobile-destiny-header">
            <span>起运：{{ result.destiny?.startAge || '-' }}岁</span>
            <span>{{ result.destiny?.isForward ? '顺行' : '逆行' }}</span>
          </div>
          <div class="mobile-destiny-scroll">
            <div class="mobile-destiny-grid">
              <div v-for="(item, index) in result.destiny?.list" :key="'dest-'+index" class="mobile-destiny-item">
                <div class="mobile-destiny-age">{{ item.age }}岁</div>
                <div class="mobile-destiny-gz">
                  <span :class="getElementClass(item.stem)">{{ item.stem }}</span>
                  <span :class="getElementClass(item.branch)">{{ item.branch }}</span>
                </div>
                <div class="mobile-destiny-sound">{{ item.takeSound }}</div>
              </div>
            </div>
          </div>
          <div class="mobile-section-subtitle">流年</div>
          <div class="mobile-destiny-scroll">
            <div class="mobile-destiny-grid">
              <div v-for="(item, index) in result.flowYears" :key="'flow-'+index" class="mobile-destiny-item">
                <div class="mobile-destiny-age">{{ item.age }}岁</div>
                <div class="mobile-destiny-gz">
                  <span :class="getStemClass(item.stemBranch)">{{ typeof item.stemBranch === 'string' ? item.stemBranch.charAt(0) : '' }}</span>
                  <span :class="getBranchClass(item.stemBranch)">{{ typeof item.stemBranch === 'string' ? item.stemBranch.charAt(1) : '' }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 移动端：AI解答 -->
        <div class="mobile-ai-section">
          <button
            class="mobile-ai-btn"
            :disabled="!result || isAnalyzing"
            @click="handleAIAnalysis"
          >
            🔮 {{ isAnalyzing ? '正在分析...' : 'AI解答' }}
          </button>
          <div v-if="hasAIAnalysis" class="mobile-ai-result" v-html="formattedAnalysis"></div>
          <!-- 移动端：分享命盘结果（排盘出来才有按钮） -->
          <button
            v-if="result.tianGan?.day"
            class="mobile-ai-btn"
            @click="shareVisible = true"
          >
            📣 分享我的命盘
          </button>
        </div>
      </template>
    </template>

    <!-- ==================== PC端模板（原有代码完整保留） ==================== -->
    <template v-else>
      <div style="display: contents;">

      <!-- 日期选择区 -->
      <MysticalCard class="input-section">
        <el-form :model="formData" label-width="80px">
          <!-- 第一行：年月日时选择 -->
          <el-row :gutter="20" class="date-row">
            <el-col :span="5">
              <el-form-item label="出生年份">
                <el-select 
                  v-model="formData.year" 
                  @change="calculateResult" 
                  placeholder="年"
                  filterable
                  clearable
                >
                  <el-option
                    v-for="year in yearOptions"
                    :key="year.value"
                    :label="year.label"
                    :value="year.value"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="5">
              <el-form-item label="月">
                <el-select 
                  v-model="formData.month" 
                  @change="calculateResult" 
                  placeholder="月"
                  clearable
                >
                  <el-option 
                    v-for="m in 12" 
                    :key="m" 
                    :label="`${m}月`"
                    :value="m"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="5">
              <el-form-item label="日">
                <el-select 
                  v-model="formData.day" 
                  @change="calculateResult" 
                  placeholder="日"
                  clearable
                >
                  <el-option 
                    v-for="d in daysInMonth" 
                    :key="d" 
                    :label="`${d}日`"
                    :value="d"
                  />
                </el-select>
              </el-form-item>
            </el-col>
            <el-col :span="5">
              <el-form-item label="时辰">
                <el-select 
                  v-model="formData.hour" 
                  @change="calculateResult" 
                  placeholder="时辰"
                  clearable
                >
                  <el-option label="子时(23:00-1:00)" value="23" />
                  <el-option label="丑时(1:00-3:00)" value="1" />
                  <el-option label="寅时(3:00-5:00)" value="3" />
                  <el-option label="卯时(5:00-7:00)" value="5" />
                  <el-option label="辰时(7:00-9:00)" value="7" />
                  <el-option label="巳时(9:00-11:00)" value="9" />
                  <el-option label="午时(11:00-13:00)" value="11" />
                  <el-option label="未时(13:00-15:00)" value="13" />
                  <el-option label="申时(15:00-17:00)" value="15" />
                  <el-option label="酉时(17:00-19:00)" value="17" />
                  <el-option label="戌时(19:00-21:00)" value="19" />
                  <el-option label="亥时(21:00-23:00)" value="21" />
                </el-select>
              </el-form-item>
            </el-col>
          </el-row>
          <!-- 第二行：性别选择 -->
          <el-row>
            <el-col :span="24">
              <div class="gender-select">
                <el-radio-group 
                  v-model="formData.gender"
                  @change="calculateResult"
                  size="large"
                >
                  <el-radio 
                    v-for="gender in ['男', '女']" 
                    :key="gender"
                    :value="gender"
                  >
                    {{ gender }}
                  </el-radio>
                </el-radio-group>
              </div>
            </el-col>
          </el-row>
        </el-form>
      </MysticalCard>

      <!-- 结果展示区 -->
      <div v-if="result" class="result-section">
        <!-- 第一行：基本信息 -->
        <el-row :gutter="20" class="info-row">
          <el-col :span="24">
            <el-card class="info-card">
              <div class="basic-info">
                <el-row :gutter="20">
                  <el-col :span="8">
                    <div class="info-group">
                      <div class="info-item">
                        <span class="label">公历：</span>
                        <span>{{ formatDate(new Date(formData.year, formData.month - 1, formData.day)) }}</span>
                      </div>
                      <div class="info-item">
                        <span class="label">农历：</span>
                        <span>{{ result.lunarDate || '-' }}</span>
                      </div>
                      <div class="info-item">
                        <span class="label">性别：</span>
                        <span>{{ formData.gender }}</span>
                      </div>
                      <div class="info-item">
                        <span class="label">生肖：</span>
                        <span>{{ result.zodiac || '-' }}</span>
                      </div>
                    </div>
                  </el-col>
                  <el-col :span="8">
                    <div class="info-group">
                      <div class="info-item">
                        <span class="label">当前节气：</span>
                        <span>{{ result.solarTerm?.current?.name || '-' }}</span>
                      </div>
                      <div class="info-item">
                        <span class="label">本月节气：</span>
                        <span>
                          <template v-if="result.solarTerm?.monthTerms?.length === 2">
                            {{ result.solarTerm.monthTermName }}[{{ result.solarTerm.monthTerms[0] }}日]，
                            {{ result.solarTerm.monthQiName }}[{{ result.solarTerm.monthTerms[1] }}日]
                          </template>
                          <template v-else>-</template>
                        </span>
                      </div>
                      <div class="info-item">
                        <span class="label">季节：</span>
                        <span>{{ result.solarTerm?.season || '-' }}</span>
                      </div>
                    </div>
                  </el-col>
                  <el-col :span="8">
                    <div class="info-group">
                      <div class="info-item">
                        <span class="label">建除十二神：</span>
                        <span>{{ result.dutyGod?.name || '无' }}</span>
                      </div>
                      <div class="info-item">
                        <span class="label">喜神：</span>
                        <span>{{ formatDirection(result.luckDirections.happiness) }}</span>
                      </div>
                      <div class="info-item">
                        <span class="label">福神：</span>
                        <span>{{ formatDirection(result.luckDirections.fortune) }}</span>
                      </div>
                      <div class="info-item">
                        <span class="label">财神：</span>
                        <span>{{ formatDirection(result.luckDirections.wealth) }}</span>
                      </div>
                    </div>
                  </el-col>
                </el-row>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- 第二行：八字信息 -->
        <el-row :gutter="20" class="info-row">
          <el-col :span="24">
            <el-card class="info-card">
              <template #header>
                <div class="card-header">
                  <span>八字信息</span>
                </div>
              </template>
              <table class="bazi-table">
                <thead>
                  <tr>
                    <th>四柱</th>
                    <th>年柱</th>
                    <th>月柱</th>
                    <th>日柱</th>
                    <th>时柱</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>十神</td>
                    <td>{{ result.shiShen?.year || '-' }}</td>
                    <td>{{ result.shiShen?.month || '-' }}</td>
                    <td>{{ result.shiShen?.day || '-' }}</td>
                    <td>{{ result.shiShen?.hour || '-' }}</td>
                  </tr>
                  <tr>
                    <td>天干</td>
                    <td>
                      <span :class="getElementClass(result.tianGan?.year)">{{ result.tianGan?.year || '-' }}</span>
                    </td>
                    <td>
                      <span :class="getElementClass(result.tianGan?.month)">{{ result.tianGan?.month || '-' }}</span>
                    </td>
                    <td>
                      <span :class="getElementClass(result.tianGan?.day)">{{ result.tianGan?.day || '-' }}</span>
                    </td>
                    <td>
                      <span :class="getElementClass(result.tianGan?.hour)">{{ result.tianGan?.hour || '-' }}</span>
                    </td>
                  </tr>
                  <tr>
                    <td>天干纳甲</td>
                    <td>
                      <span :class="getElementClass(result.tianGanNaJia?.year)">{{ result.tianGanNaJia?.year || '-' }}</span>
                    </td>
                    <td>
                      <span :class="getElementClass(result.tianGanNaJia?.month)">{{ result.tianGanNaJia?.month || '-' }}</span>
                    </td>
                    <td>
                      <span :class="getElementClass(result.tianGanNaJia?.day)">{{ result.tianGanNaJia?.day || '-' }}</span>
                    </td>
                    <td>
                      <span :class="getElementClass(result.tianGanNaJia?.hour)">{{ result.tianGanNaJia?.hour || '-' }}</span>
                    </td>
                  </tr>
                  <tr>
                    <td>地支</td>
                    <td>
                      <span :class="getElementClass(result.diZhi?.year)">{{ result.diZhi?.year || '-' }}</span>
                    </td>
                    <td>
                      <span :class="getElementClass(result.diZhi?.month)">{{ result.diZhi?.month || '-' }}</span>
                    </td>
                    <td>
                      <span :class="getElementClass(result.diZhi?.day)">{{ result.diZhi?.day || '-' }}</span>
                    </td>
                    <td>
                      <span :class="getElementClass(result.diZhi?.hour)">{{ result.diZhi?.hour || '-' }}</span>
                    </td>
                  </tr>
                  <tr>
                    <td>地支关系</td>
                    <td>
                      <div class="vertical-list" style="text-align: center;">
                        <template v-if="result.diZhiRelations?.year">
                          <template v-if="result.diZhiRelations.year.triad?.length">
                            <el-tag type="success" class="relation-tag">
                              三合[{{ result.diZhiRelations.year.triad.map(b => b.name).join(',') }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.year.group6">
                            <el-tag type="warning" class="relation-tag">
                              六合[{{ result.diZhiRelations.year.group6.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.year.punishing">
                            <el-tag type="danger" class="relation-tag">
                              刑[{{ result.diZhiRelations.year.punishing.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.year.conflict">
                            <el-tag type="danger" class="relation-tag">
                              冲[{{ result.diZhiRelations.year.conflict.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.year.harming">
                            <el-tag type="info" class="relation-tag">
                              害[{{ result.diZhiRelations.year.harming.name }}]
                            </el-tag>
                          </template>
                        </template>
                        <template v-else>-</template>
                      </div>
                    </td>
                    <td>
                      <div class="vertical-list" style="text-align: center;">
                        <template v-if="result.diZhiRelations?.month">
                          <template v-if="result.diZhiRelations.month.triad?.length">
                            <el-tag type="success" class="relation-tag">
                              三合[{{ result.diZhiRelations.month.triad.map(b => b.name).join(',') }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.month.group6">
                            <el-tag type="warning" class="relation-tag">
                              六合[{{ result.diZhiRelations.month.group6.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.month.punishing">
                            <el-tag type="danger" class="relation-tag">
                              刑[{{ result.diZhiRelations.month.punishing.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.month.conflict">
                            <el-tag type="danger" class="relation-tag">
                              冲[{{ result.diZhiRelations.month.conflict.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.month.harming">
                            <el-tag type="info" class="relation-tag">
                              害[{{ result.diZhiRelations.month.harming.name }}]
                            </el-tag>
                          </template>
                        </template>
                        <template v-else>-</template>
                      </div>
                    </td>
                    <td>
                      <div class="vertical-list" style="text-align: center;">
                        <template v-if="result.diZhiRelations?.day">
                          <template v-if="result.diZhiRelations.day.triad?.length">
                            <el-tag type="success" class="relation-tag">
                              三合[{{ result.diZhiRelations.day.triad.map(b => b.name).join(',') }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.day.group6">
                            <el-tag type="warning" class="relation-tag">
                              六合[{{ result.diZhiRelations.day.group6.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.day.punishing">
                            <el-tag type="danger" class="relation-tag">
                              刑[{{ result.diZhiRelations.day.punishing.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.day.conflict">
                            <el-tag type="danger" class="relation-tag">
                              冲[{{ result.diZhiRelations.day.conflict.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.day.harming">
                            <el-tag type="info" class="relation-tag">
                              害[{{ result.diZhiRelations.day.harming.name }}]
                            </el-tag>
                          </template>
                        </template>
                        <template v-else>-</template>
                      </div>
                    </td>
                    <td>
                      <div class="vertical-list" style="text-align: center;">
                        <template v-if="result.diZhiRelations?.hour">
                          <template v-if="result.diZhiRelations.hour.triad?.length">
                            <el-tag type="success" class="relation-tag">
                              三合[{{ result.diZhiRelations.hour.triad.map(b => b.name).join(',') }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.hour.group6">
                            <el-tag type="warning" class="relation-tag">
                              六合[{{ result.diZhiRelations.hour.group6.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.hour.punishing">
                            <el-tag type="danger" class="relation-tag">
                              刑[{{ result.diZhiRelations.hour.punishing.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.hour.conflict">
                            <el-tag type="danger" class="relation-tag">
                              冲[{{ result.diZhiRelations.hour.conflict.name }}]
                            </el-tag>
                          </template>
                          <template v-if="result.diZhiRelations.hour.harming">
                            <el-tag type="info" class="relation-tag">
                              害[{{ result.diZhiRelations.hour.harming.name }}]
                            </el-tag>
                          </template>
                        </template>
                        <template v-else>-</template>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>藏干</td>
                    <td>
                      <div class="vertical-list" style="text-align: center;" v-html="formatCangGanVertical(result.cangGan?.year)">
                      </div>
                    </td>
                    <td>
                      <div class="vertical-list" style="text-align: center;" v-html="formatCangGanVertical(result.cangGan?.month)">
                      </div>
                    </td>
                    <td>
                      <div class="vertical-list" style="text-align: center;" v-html="formatCangGanVertical(result.cangGan?.day)">
                      </div>
                    </td>
                    <td>
                      <div class="vertical-list" style="text-align: center;" v-html="formatCangGanVertical(result.cangGan?.hour)">
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td>纳音</td>
                    <td>{{ result.naYin?.year || '-' }}</td>
                    <td>{{ result.naYin?.month || '-' }}</td>
                    <td>{{ result.naYin?.day || '-' }}</td>
                    <td>{{ result.naYin?.hour || '-' }}</td>
                  </tr>
                  <tr>
                    <td>空亡</td>
                    <td>{{ result.kongWang?.year || '-' }}</td>
                    <td>{{ result.kongWang?.month || '-' }}</td>
                    <td>{{ result.kongWang?.day || '-' }}</td>
                    <td>{{ result.kongWang?.hour || '-' }}</td>
                  </tr>
                  <tr>
                    <td>长生</td>
                    <td>{{ result.lifeGods?.year?.name || '-' }}</td>
                    <td>{{ result.lifeGods?.month?.name || '-' }}</td>
                    <td>{{ result.lifeGods?.day?.name || '-' }}</td>
                    <td>{{ result.lifeGods?.hour?.name || '-' }}</td>
                  </tr>
                  <tr>
                    <td>神煞</td>
                    <td>
                      <div class="vertical-list" style="text-align: center;" v-html="formatGodsVertical(result.yearGods)">
                      </div>
                    </td>
                    <td>
                      <div class="vertical-list" style="text-align: center;" v-html="formatGodsVertical(result.monthGods)">
                      </div>
                    </td>
                    <td>
                      <div class="vertical-list" style="text-align: center;" v-html="formatGodsVertical(result.dayGods)">
                      </div>
                    </td>
                    <td>
                      <div class="vertical-list" style="text-align: center;" v-html="formatGodsVertical(result.hourGods)">
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </el-card>
          </el-col>
        </el-row>

        <!-- 第三行：阴阳五行 -->
        <el-row :gutter="20" class="info-row">
          <el-col :span="24">
            <el-card class="info-card">
              <template #header>
                <div class="card-header">
                  <span>阴阳五行</span>
                </div>
              </template>
              <div class="yinyang-wuxing-info">
                <div class="info-group">
                  <!-- 第一行：日主 -->
                  <div class="info-row">
                    <div class="info-item full-width">
                      <span class="label">日主：</span>
                      <span>{{ result.dayMaster?.element }}{{ result.dayMaster?.strength > 0 ? '旺' : '弱' }} (得分:{{ result.dayMaster?.strength || 0 }})</span>
                    </div>
                  </div>
                  
                  <!-- 第二行：八字五行和八字阴阳 -->
                  <div class="info-row">
                    <div class="info-item">
                      <span class="label">八字五行：</span>
                      <span>
                        金{{ result.elements['金'] }}个，
                        木{{ result.elements['木'] }}个，
                        水{{ result.elements['水'] }}个，
                        火{{ result.elements['火'] }}个，
                        土{{ result.elements['土'] }}个
                      </span>
                    </div>
                    <div class="info-item">
                      <span class="label">八字阴阳：</span>
                      <span>
                        阴{{ result.yinYangCount?.yin || 0 }}个，
                        阳{{ result.yinYangCount?.yang || 0 }}个，
                        阴占比{{ formatPercent(result.yinYangCount?.yin, result.yinYangCount?.total) }}，
                        阳占比{{ formatPercent(result.yinYangCount?.yang, result.yinYangCount?.total) }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- 第三行：藏干五行和藏干阴阳 -->
                  <div class="info-row">
                    <div class="info-item">
                      <span class="label">藏干五行：</span>
                      <span>
                        金{{ result.hiddenElements?.['金'] || 0 }}个，
                        木{{ result.hiddenElements?.['木'] || 0 }}个，
                        水{{ result.hiddenElements?.['水'] || 0 }}个，
                        火{{ result.hiddenElements?.['火'] || 0 }}个，
                        土{{ result.hiddenElements?.['土'] || 0 }}个
                      </span>
                    </div>
                    <div class="info-item">
                      <span class="label">藏干阴阳：</span>
                      <span>
                        阴{{ result.hiddenYinYangCount?.yin || 0 }}个，
                        阳{{ result.hiddenYinYangCount?.yang || 0 }}个，
                        阴占比{{ formatPercent(result.hiddenYinYangCount?.yin, result.hiddenYinYangCount?.total) }}，
                        阳占比{{ formatPercent(result.hiddenYinYangCount?.yang, result.hiddenYinYangCount?.total) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- 大运流年卡片 -->
        <el-row :gutter="20" class="info-row">
          <el-col :span="24">
            <el-card class="info-card">
              <template #header>
                <div class="card-header">
                  <span>大运流年</span>
                </div>
              </template>
              <div class="destiny-info">
                <!-- 基本信息 -->
                <div class="info-group">
                  <div class="info-item">
                    <span class="label">大运起始年龄：</span>
                    <span>{{ result.destiny?.startAge || '-' }}岁</span>
                    <span class="label ml-4">交运时间：</span>
                    <span>{{ result.destiny?.changeYear ? formatDate(result.destiny.changeYear) : '-' }}</span>
                    <span class="label ml-4">大运顺序：</span>
                    <span>{{ result.destiny?.isForward ? '顺行' : '逆行' }}</span>
                  </div>
                </div>

                <!-- 大运流年卡片 -->
                <div class="destiny-grid">
                  <!-- 日期行 -->
                  <div class="grid-row">
                    <div class="grid-cell label">日期</div>
                    <div v-for="(item, index) in result.destiny?.list" :key="'date-'+index" class="grid-cell">
                      {{ item.age }}岁<br>{{ item.year }}
                    </div>
                  </div>
                  <!-- 大运行 -->
                  <div class="grid-row">
                    <div class="grid-cell label">大运</div>
                    <div v-for="(item, index) in result.destiny?.list" :key="'destiny-'+index" class="grid-cell">
                      <span :class="getElementClass(item.stem)">{{ item.stem }}</span>
                      <span :class="getElementClass(item.branch)">{{ item.branch }}</span>
                    </div>
                  </div>
                  <!-- 空亡行 -->
                  <div class="grid-row">
                    <div class="grid-cell label">空亡</div>
                    <div v-for="(item, index) in result.destiny?.list" :key="'missing-'+index" class="grid-cell">
                      {{ item.missing }}
                    </div>
                  </div>
                  <!-- 纳音行 -->
                  <div class="grid-row">
                    <div class="grid-cell label">纳音</div>
                    <div v-for="(item, index) in result.destiny?.list" :key="'sound-'+index" class="grid-cell">
                      {{ item.takeSound }}
                    </div>
                  </div>
                  <!-- 运年行 -->
                  <div class="grid-row">
                    <div class="grid-cell label">运年</div>
                    <div v-for="(item, index) in result.flowYears" :key="'flow-year-'+index" class="grid-cell">
                      {{ item.age }}岁<br>{{ item.year }}
                    </div>
                  </div>
                  <!-- 流年行 -->
                  <div class="grid-row">
                    <div class="grid-cell label">流年</div>
                    <div v-for="(item, index) in result.flowYears" :key="'flow-'+index" class="grid-cell">
                      <span :class="getStemClass(item.stemBranch)">{{ typeof item.stemBranch === 'string' ? item.stemBranch.charAt(0) : '' }}</span>
                      <span :class="getBranchClass(item.stemBranch)">{{ typeof item.stemBranch === 'string' ? item.stemBranch.charAt(1) : '' }}</span>
                    </div>
                  </div>
                  <!-- 小运行 -->
                  <div class="grid-row">
                    <div class="grid-cell label">小运</div>
                    <div v-for="(item, index) in result.flowYears" :key="'small-'+index" class="grid-cell">
                      <span :class="getBranchClass(item.stemBranch)">{{ typeof item.stemBranch === 'string' ? item.stemBranch.charAt(1) : '' }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </el-card>
          </el-col>
        </el-row>

        <!-- AI解答按钮和结果 -->
        <div class="ai-analysis-section">
          <MysticalButton 
            @click="handleAIAnalysis"
            :disabled="!result || isAnalyzing"
            class="analyze-button"
          >
            <template #icon>
              <span class="icon">🔮</span>
            </template>
            {{ isAnalyzing ? '正在分析...' : 'AI解答' }}
          </MysticalButton>

          <!-- AI分析结果 -->
          <div v-if="hasAIAnalysis" class="analysis-result">
            <AIResultCard
              title="八字分析"
              subtitle="命理解读与人生建议"
              icon="crystal-ball"
              :content="formattedAnalysis"
              :loading="isAnalyzing"
              :status="isAnalyzing ? '分析中...' : '分析完成'"
              class="result-card"
            >
              <template #loading>
                <div class="loading-animation">
                  <div class="crystal-ball">🔮</div>
                  <p>正在进行八字分析...</p>
                </div>
              </template>
              <template #content>
                <div class="analysis-text" v-html="formattedAnalysis"></div>
              </template>
            </AIResultCard>
          </div>

          <!-- PC端：分享命盘结果（排盘出来才有按钮） -->
          <MysticalButton
            v-if="result.tianGan?.day"
            class="analyze-button"
            @click="shareVisible = true"
          >
            <template #icon>
              <span class="icon">📣</span>
            </template>
            分享我的命盘
          </MysticalButton>
        </div>
      </div>
      </div>
    </template>

    <!-- 结果分享卡 -->
    <ShareResult
      v-model:visible="shareVisible"
      scene-label="八字排盘"
      :headline="shareHeadline"
      :subline="shareSubline"
      :tags="shareTags"
      :symbol="shareSymbol"
      page="bazi"
    />

    <!-- 积分不足充值引导（usePaymentGuard.checkPoints 触发） -->
    <PayDialog
      v-model:visible="showPayDialog"
      :cost="payDialogCost"
      :balance="payDialogBalance"
      :feature="payDialogFeature"
      @goPoints="goToPoints"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, watch } from 'vue'
import { ElMessage } from 'element-plus'
import lunisolar from 'lunisolar'
import zhCn from 'lunisolar/locale/zh-cn'
import { theGods } from '@lunisolar/plugin-thegods'
import { takeSound } from '@lunisolar/plugin-takesound'
import { char8ex } from '@lunisolar/plugin-char8ex'
import { MysticalCard, ShareResult } from '@/components/common'
import MysticalButton from '@/components/common/MysticalButton.vue'
import AIResultCard from '@/components/common/AIResultCard.vue'
import { analyzeBazi } from '@/services/ai/bazi.js'
import { marked } from 'marked'
import { API_CONFIG } from '@/config'
import { useMobile } from '@/composables/useMobile'
import { useAuthGuard } from '@/composables/useAuthGuard'
import { usePaymentGuard } from '@/composables/usePaymentGuard'
import PayDialog from '@/components/common/PayDialog.vue'

const { isMobile } = useMobile()
const { requireLogin } = useAuthGuard()
const { showPayDialog, payDialogCost, payDialogBalance, payDialogFeature, checkPoints, goToPoints } = usePaymentGuard()

// 五行传统色映射
const wuxingColors = {
  '金': '#FFD700',
  '木': '#4CAF50',
  '水': '#2196F3',
  '火': '#F44336',
  '土': '#795548'
}

// 五行属性文字映射（天干地支→五行属性）
const ganWuxingMap = {
  '甲': '阳木', '乙': '阴木', '丙': '阳火', '丁': '阴火',
  '戊': '阳土', '己': '阴土', '庚': '阳金', '辛': '阴金',
  '壬': '阳水', '癸': '阴水',
  '子': '阳水', '丑': '阴土', '寅': '阳木', '卯': '阴木',
  '辰': '阳土', '巳': '阴火', '午': '阳火', '未': '阴土',
  '申': '阳金', '酉': '阴金', '戌': '阳土', '亥': '阴水'
}

// 四柱信息计算属性（供移动端模板使用）
const pillarData = computed(() => {
  if (!result.value) return []
  const r = result.value
  return [
    { label: '年柱', stem: r.tianGan?.year, branch: r.diZhi?.year, shiShen: r.shiShen?.year, cangGan: r.cangGan?.year, naYin: r.naYin?.year, kongWang: r.kongWang?.year, lifeGod: r.lifeGods?.year?.name, gods: r.yearGods, relations: r.diZhiRelations?.year },
    { label: '月柱', stem: r.tianGan?.month, branch: r.diZhi?.month, shiShen: r.shiShen?.month, cangGan: r.cangGan?.month, naYin: r.naYin?.month, kongWang: r.kongWang?.month, lifeGod: r.lifeGods?.month?.name, gods: r.monthGods, relations: r.diZhiRelations?.month },
    { label: '日柱', stem: r.tianGan?.day, branch: r.diZhi?.day, shiShen: r.shiShen?.day, cangGan: r.cangGan?.day, naYin: r.naYin?.day, kongWang: r.kongWang?.day, lifeGod: r.lifeGods?.day?.name, gods: r.dayGods, relations: r.diZhiRelations?.day },
    { label: '时柱', stem: r.tianGan?.hour, branch: r.diZhi?.hour, shiShen: r.shiShen?.hour, cangGan: r.cangGan?.hour, naYin: r.naYin?.hour, kongWang: r.kongWang?.hour, lifeGod: r.lifeGods?.hour?.name, gods: r.hourGods, relations: r.diZhiRelations?.hour }
  ]
})

// 五行最大值（进度条百分比计算用）
const wuxingMax = computed(() => {
  if (!result.value?.elements) return 1
  const vals = Object.values(result.value.elements)
  return Math.max(...vals, 1)
})

// 格式化藏干为一行文字
const formatCangGanText = (cangGan) => {
  if (!cangGan || !cangGan.length) return '-'
  return cangGan.map(item => item.name).join('、')
}

// 格式化地支关系为一行标签
const formatRelationsText = (relations) => {
  if (!relations) return ''
  const tags = []
  if (relations.triad?.length) tags.push({ text: '三合', type: 'success' })
  if (relations.group6) tags.push({ text: '六合', type: 'warning' })
  if (relations.punishing) tags.push({ text: '刑', type: 'danger' })
  if (relations.conflict) tags.push({ text: '冲', type: 'danger' })
  if (relations.harming) tags.push({ text: '害', type: 'info' })
  return tags
}

// 配置 marked 选项
marked.setOptions({
  gfm: true,
  breaks: true,
  mangle: false,
  headerIds: false,
  smartLists: true,
  smartypants: true
})

// 初始化 lunisolar
lunisolar.locale(zhCn)
// 注册插件到 lunisolar
lunisolar.extend(theGods)
lunisolar.extend(takeSound)
lunisolar.extend(char8ex)

// 表单数据
const formData = reactive({
  year: new Date().getFullYear(),
  month: new Date().getMonth() + 1,
  day: new Date().getDate(),
  hour: String(Math.floor(new Date().getHours() / 2) * 2 + 1),
  gender: '男'  // 性别只能是 '男' 或 '女'
})

// 静默判断：是否加载了用户自己的生日
const isOwnBirthday = ref(false)

// 计算当月天数
const daysInMonth = computed(() => {
  if (!formData.year || !formData.month) return []
  
  const daysCount = new Date(formData.year, formData.month, 0).getDate()
  return Array.from({ length: daysCount }, (_, i) => i + 1)
})

// 添加计算年份选项的计算属性
const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear()
  const years = []
  for (let i = currentYear - 100; i <= currentYear; i++) {
    years.push({
      value: i,
      label: `${i}年`
    })
  }
  return years
})

// 计算结果
const result = ref({
  // 基本信息
  lunarDate: '',
  zodiac: '',
  solarTerm: '',
  currentTerm: null,
  prevTerm: null,
  prevQi: null,
  season: '',
  monthTerms: [],

  // 八字信息
  shiShen: {
    year: '',
    month: '',
    day: '',
    hour: ''
  },
  tianGan: {
    year: '',
    month: '',
    day: '',
    hour: ''
  },
  diZhi: {
    year: '',
    month: '',
    day: '',
    hour: ''
  },
  cangGan: {
    year: [],
    month: [],
    day: [],
    hour: []
  },
  naYin: {
    year: '',
    month: '',
    day: '',
    hour: ''
  },
  kongWang: {
    year: '',
    month: '',
    day: '',
    hour: ''
  },

  // 五行分析
  dayMaster: { element: '', strength: 0 },
  elements: { '金': 0, '木': 0, '水': 0, '火': 0, '土': 0 },
  elementScores: { '金': 0, '木': 0, '水': 0, '火': 0, '土': 0 },

  // 神煞信息
  dutyGod: null,
  yearGods: [],
  monthGods: [],
  dayGods: [],
  hourGods: [],
  goodGods: [],
  badGods: [],

  // 吉神方位
  luckDirections: {
    happiness: { direction: '', description: '' },
    fortune: { direction: '', description: '' },
    wealth: { direction: '', description: '' }
  },

  // 天干关系
  tianGanRelations: {
    year: [],
    month: [],
    day: [],
    hour: []
  },
  // 地支关系
  diZhiRelations: {},

  // 阴阳统计
  yinYangCount: { yin: 0, yang: 0, total: 8 },
  hiddenYinYangCount: { yin: 0, yang: 0, total: 0 },

  // 长生十二神
  lifeGods: { year: null, month: null, day: null, hour: null },

  // 大运流年
  destiny: { startAge: 0, list: [] },

  // 天干纳甲
  tianGanNaJia: {
    year: '',
    month: '',
    day: '',
    hour: ''
  }
})

// 加载用户资料中的生日信息


// 在组件挂载时自动计算一次
// 加载登录用户的出生信息
const loadUserProfile = async () => {
  try {
    const token = localStorage.getItem('token')
    if (!token) {
      console.log('未登录，使用当前日期')
      return
    }
    const res = await fetch(`${API_CONFIG.baseUrl}${API_CONFIG.endpoints.persona.profile}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    const data = await res.json()
    if (data.success && !data.data.is_guest && data.data.birth_info) {
      const birth = data.data.birth_info
      if (birth.year && birth.month && birth.day) {
        formData.year = birth.year
        formData.month = birth.month
        formData.day = birth.day
        if (birth.hour !== null && birth.hour !== undefined) {
          formData.hour = String(birth.hour)
        }
        isOwnBirthday.value = true  // 标记为加载了用户自己的生日
        console.log('已加载用户出生信息:', birth)
      }
    }
  } catch (e) { console.error('加载用户资料失败', e) }
}


/* ---------- 结果分享卡（文案全部来自已有排盘 state, 不发新请求） ---------- */
const shareVisible = ref(false)
const shareHeadline = computed(() => {
  const r = result.value
  const stem = r?.tianGan?.day
  const el = r?.dayMaster?.element
  if (!stem && !el) return '我的八字排盘'
  return `我的日主是「${stem && el ? `${stem}${el}` : (stem || el)}」`
})
const shareSubline = computed(() => {
  const r = result.value
  const parts = []
  if (r?.zodiac) parts.push(`属${r.zodiac}`)
  if (r?.dayMaster?.element) parts.push(`日主${r.dayMaster.element}${r.dayMaster.strength > 0 ? '旺' : '弱'}`)
  return parts.join(' · ')
})
const shareTags = computed(() => {
  const els = result.value?.elements
  if (!els) return []
  // 五行分布 Top4（个数为 0 的也如实展示）
  return Object.entries(els)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 4)
    .map(([k, v]) => `${k}×${v}`)
})
// 视觉主体: 四柱(年月日时), 2+2 对称断行「乙亥 甲申\n庚午 辛巳」
const shareSymbol = computed(() => {
  const r = result.value
  if (!r?.tianGan?.day) return ''
  const pillars = ['year', 'month', 'day', 'hour']
    .map(k => `${r.tianGan?.[k] || ''}${r.diZhi?.[k] || ''}`)
  return `${pillars[0]} ${pillars[1]}\n${pillars[2]} ${pillars[3]}`
})

onMounted(async () => {
  await loadUserProfile()
  // 先尝试加载用户资料
  calculateResult()
})

// 计算结果方法
const calculateResult = () => {
  try {
    if (!formData.year || !formData.month || !formData.day) {
      ElMessage.warning('请选择完整的出生日期')
      return
    }

    const date = new Date(formData.year, formData.month - 1, formData.day)
    date.setHours(parseInt(formData.hour))
    const lsr = lunisolar(date)
    const c8ex = lsr.char8ex(formData.gender === '男' ? 1 : 0)

    // 定义地支生肖的对关系
    const zodiacMap = {
      '子': '鼠',
      '丑': '牛',
      '寅': '虎',
      '卯': '兔',
      '辰': '龙',
      '巳': '蛇',
      '午': '马',
      '未': '羊',
      '申': '猴',
      '酉': '鸡',
      '戌': '狗',
      '亥': '猪'
    }

    result.value = {
      // 基本信息
      lunarDate: `${lsr.lunar.getYearName()}年${lsr.lunar.getMonthName()}${lsr.lunar.getDayName()}`,
      zodiac: zodiacMap[lsr.char8.year.branch.name] || '-',
      solarTerm: lsr.solarTerm?.toString() || '',

      // 八字信息
      shiShen: {
        year: c8ex.year.stemTenGod?.name || '',
        month: c8ex.month.stemTenGod?.name || '',
        day: c8ex.day.stemTenGod?.name || '',
        hour: c8ex.hour.stemTenGod?.name || ''
      },
      tianGan: {
        year: c8ex.year.stem.name,
        month: c8ex.month.stem.name,
        day: c8ex.day.stem.name,
        hour: c8ex.hour.stem.name
      },
      diZhi: {
        year: c8ex.year.branch.name,
        month: c8ex.month.branch.name,
        day: c8ex.day.branch.name,
        hour: c8ex.hour.branch.name
      },
      cangGan: {
        year: lsr.char8.year.branch.hiddenStems.map((stem, index) => ({
          name: `${stem.name} ${c8ex.year.branchTenGod[index]?.name || ''}`,
          type: 'info'
        })),
        month: lsr.char8.month.branch.hiddenStems.map((stem, index) => ({
          name: `${stem.name} ${c8ex.month.branchTenGod[index]?.name || ''}`,
          type: 'info'
        })),
        day: lsr.char8.day.branch.hiddenStems.map((stem, index) => ({
          name: `${stem.name} ${c8ex.day.branchTenGod[index]?.name || ''}`,
          type: 'info'
        })),
        hour: lsr.char8.hour.branch.hiddenStems.map((stem, index) => ({
          name: `${stem.name} ${c8ex.hour.branchTenGod[index]?.name || ''}`,
          type: 'info'
        }))
      },
      naYin: {
        year: c8ex.year.takeSound?.toString() || '',
        month: c8ex.month.takeSound?.toString() || '',
        day: c8ex.day.takeSound?.toString() || '',
        hour: c8ex.hour.takeSound?.toString() || ''
      },
      kongWang: {
        year: c8ex.year.missing?.map(b => b.name).join('') || '',
        month: c8ex.month.missing?.map(b => b.name).join('') || '',
        day: c8ex.day.missing?.map(b => b.name).join('') || '',
        hour: c8ex.hour.missing?.map(b => b.name).join('') || ''
      },

      // 五行分析
      dayMaster: {
        element: lsr.char8.me.e5.name,
        strength: Math.round(lsr.char8.me.strength * 100) / 100
      },
      elements: {
        '金': 0,
        '木': 0,
        '水': 0,
        '火': 0,
        '土': 0
      },
      elementScores: {
        '金': 0,
        '木': 0,
        '水': 0,
        '火': 0,
        '土': 0
      },

      // 神煞信息
      dutyGod: lsr.theGods.getDuty12God(),
      yearGods: lsr.theGods.getGods('Y'),
      monthGods: lsr.theGods.getGods('M'),
      dayGods: lsr.theGods.getGods('D'),
      hourGods: lsr.theGods.getGods('H'),
      goodGods: lsr.theGods.getGoodGods('MD'),
      badGods: lsr.theGods.getBadGods('MD'),

      // 吉神方位
      luckDirections: lsr.theGods.getAllLuckDirection().reduce((acc, [d24, god]) => {
        if (god.name.includes('喜神')) acc.happiness = { direction: d24.direction, description: god.name }
        else if (god.name.includes('福神')) acc.fortune = { direction: d24.direction, description: god.name }
        else if (god.name.includes('财神')) acc.wealth = { direction: d24.direction, description: god.name }
        return acc
      }, {
        happiness: { direction: '', description: '' },
        fortune: { direction: '', description: '' },
        wealth: { direction: '', description: '' }
      }),

      // 天干关系
      tianGanRelations: {
        year: lsr.char8.year.stem.relations,
        month: lsr.char8.month.stem.relations,
        day: lsr.char8.day.stem.relations,
        hour: lsr.char8.hour.stem.relations
      },
      // 地支关系
      diZhiRelations: {
        year: {
          triad: lsr.char8.year.branch.triad,
          group6: lsr.char8.year.branch.group6,
          punishing: lsr.char8.year.branch.punishing,
          conflict: lsr.char8.year.branch.conflict,
          harming: lsr.char8.year.branch.harming
        },
        month: {
          triad: lsr.char8.month.branch.triad,
          group6: lsr.char8.month.branch.group6,
          punishing: lsr.char8.month.branch.punishing,
          conflict: lsr.char8.month.branch.conflict,
          harming: lsr.char8.month.branch.harming
        },
        day: {
          triad: lsr.char8.day.branch.triad,
          group6: lsr.char8.day.branch.group6,
          punishing: lsr.char8.day.branch.punishing,
          conflict: lsr.char8.day.branch.conflict,
          harming: lsr.char8.day.branch.harming
        },
        hour: {
          triad: lsr.char8.hour.branch.triad,
          group6: lsr.char8.hour.branch.group6,
          punishing: lsr.char8.hour.branch.punishing,
          conflict: lsr.char8.hour.branch.conflict,
          harming: lsr.char8.hour.branch.harming
        }
      },
      // 阴阳统计
      yinYangCount: {
        yin: 0,
        yang: 0,
        total: 8
      },
      hiddenYinYangCount: {
        yin: 0,
        yang: 0,
        total: 0
      },
      // 长生十二神
      lifeGods: {
        year: lsr.theGods.getLife12God('year'),
        month: lsr.theGods.getLife12God('month'),
        day: lsr.theGods.getLife12God('day'),
        hour: lsr.theGods.getLife12God('hour')
      },

      // 节气信息
      currentTerm: lsr.solarTerm,
      prevTerm: lsr.recentSolarTerm(0),
      prevQi: lsr.recentSolarTerm(1),
      season: lsr.getSeason(),

      // 获取当月节气
      monthTerms: lunisolar.SolarTerm.getMonthTerms(formData.year, formData.month),

      // 大运流年
      destiny: {
        startAge: 0,
        list: []
      },

      // 天干纳甲
      tianGanNaJia: {
        year: lsr.char8.year.stem.trigram8?.name || '',
        month: lsr.char8.month.stem.trigram8?.name || '',
        day: lsr.char8.day.stem.trigram8?.name || '',
        hour: lsr.char8.hour.stem.trigram8?.name || ''
      }
    }

    // 计算五行得分
    const stems = [
      lsr.char8.year.stem,
      lsr.char8.month.stem,
      lsr.char8.day.stem,
      lsr.char8.hour.stem
    ]
    const branches = [
      lsr.char8.year.branch,
      lsr.char8.month.branch,
      lsr.char8.day.branch,
      lsr.char8.hour.branch
    ]

    // 初始化五行统计
    result.value.elements = {
      '金': 0,
      '木': 0,
      '水': 0,
      '火': 0,
      '土': 0
    }

    // 初始化藏干五行统计
    result.value.hiddenElements = {
      '金': 0,
      '木': 0,
      '水': 0,
      '火': 0,
      '土': 0
    }

    // 统计天干五行
    stems.forEach(stem => {
      const element = stem.e5?.name
      if (element && result.value.elements[element] !== undefined) {
        result.value.elements[element]++
      }
    })

    // 统计地支五行
    branches.forEach(branch => {
      const element = branch.e5?.name
      if (element && result.value.elements[element] !== undefined) {
        result.value.elements[element]++
      }
    })

    // 统计藏干五行
    const allHiddenStems = [
      ...lsr.char8.year.branch.hiddenStems,
      ...lsr.char8.month.branch.hiddenStems,
      ...lsr.char8.day.branch.hiddenStems,
      ...lsr.char8.hour.branch.hiddenStems
    ]

    allHiddenStems.forEach(stem => {
      const element = stem.e5?.name
      if (element && result.value.hiddenElements[element] !== undefined) {
        result.value.hiddenElements[element]++
      }
    })

    // 计算阴阳统计
    const yinYangCount = {
      yin: 0,
      yang: 0,
      total: 8  // 四柱天干地支共8个字
    }

    // 统计天干的阴阳
    stems.forEach(stem => {
      const stemName = stem.name
      // 根据天干名称判断阴阳
      if (['乙', '丁', '己', '辛', '癸'].includes(stemName)) {
        yinYangCount.yin++
      } else if (['甲', '丙', '戊', '庚', '壬'].includes(stemName)) {
        yinYangCount.yang++
      }
    })

    // 统计地支的阴阳
    branches.forEach(branch => {
      const branchName = branch.name
      // 根据地支名称判断阴阳
      if (['丑', '卯', '巳', '未', '酉', '亥'].includes(branchName)) {
        yinYangCount.yin++
      } else if (['子', '寅', '辰', '午', '申', '戌'].includes(branchName)) {
        yinYangCount.yang++
      }
    })

    // 更新阴阳统计结果
    result.value.yinYangCount = yinYangCount

    // 计算藏干阴阳统计
    const hiddenYinYangCount = {
      yin: 0,
      yang: 0,
      total: allHiddenStems.length
    }

    // 统计藏干阴阳
    allHiddenStems.forEach(stem => {
      const stemName = stem.name
      // 根据天干名称判断阴阳
      if (['乙', '丁', '己', '辛', '癸'].includes(stemName)) {
        hiddenYinYangCount.yin++
      } else if (['甲', '丙', '戊', '庚', '壬'].includes(stemName)) {
        hiddenYinYangCount.yang++
      }
    })

    // 输出调试信息
    console.log('八字五行统计:', {
      天干: stems.map(stem => ({
        字: stem.name,
        五行: stem.e5?.name
      })),
      地支: branches.map(branch => ({
        字: branch.name,
        五行: branch.e5?.name
      })),
      统计: result.value.elements
    })

    console.log('藏干五行统计:', {
      藏干: allHiddenStems.map(stem => ({
        字: stem.name,
        五行: stem.e5?.name
      })),
      统计: result.value.hiddenElements
    })

    console.log('八字阴阳统计:', {
      天干: stems.map(stem => ({
        字: stem.name,
        阴阳: ['乙', '丁', '己', '辛', '癸'].includes(stem.name) ? '阴' : '阳'
      })),
      地支: branches.map(branch => ({
        字: branch.name,
        阴阳: ['丑', '卯', '巳', '未', '酉', '亥'].includes(branch.name) ? '阴' : '阳'
      })),
      统计: yinYangCount
    })

    console.log('藏干阴阳统计:', {
      藏干: allHiddenStems.map(stem => ({
        字: stem.name,
        阴阳: ['乙', '丁', '己', '辛', '癸'].includes(stem.name) ? '阴' : '阳'
      })),
      统计: hiddenYinYangCount
    })

    // 更新结果
    result.value.hiddenYinYangCount = hiddenYinYangCount

    // 计算节气信息
    try {
      // 获取当前节气
      const currentTerm = lsr.solarTerm
      
      // 获取当月节气日期 [节, 气]
      const monthTerms = lunisolar.SolarTerm.getMonthTerms(formData.year, formData.month)
      
      // 获取节气名称列表
      const termNames = lunisolar.SolarTerm.getNames()
      
      // 计算当月节气的索引和名称
      const monthIndex = formData.month - 1  // 转换为0基索引
      const termIndex = monthIndex * 2       // 每月有两个节气
      const termName = termNames[termIndex]  // 当月的节
      const qiName = termNames[termIndex + 1]  // 当月的气

      console.log('节气计算信息:', {
        输入日期: `${formData.year}年${formData.month}月${formData.day}日`,
        当前节气: currentTerm?.name,
        本月节气: monthTerms,
        本月节名: termName,
        本月气名: qiName
      })
      
      // 更新节气信息
      result.value.solarTerm = {
        current: currentTerm,           // 当前节气（如果当天是节气日）
        monthTerms,                     // 当月节气日期 [节, 气]
        monthTermName: termName,        // 当月节名
        monthQiName: qiName            // 当月气名
      }

      // 计算大运
      const isYangDay = lsr.char8.day.stem.yinYang === 1
      const isForward = (formData.gender === '男' && isYangDay) || 
                       (formData.gender === '女' && !isYangDay)

      // 计算起运时间
      let startingAge = 0
      let changeYear = null
      const birthDate = new Date(formData.year, formData.month - 1, formData.day)
      birthDate.setHours(parseInt(formData.hour) || 0)

      if (monthTerms && monthTerms.length === 2) {
        // 获取当月的节和气的日期
        const [nodeDay, qiDay] = monthTerms
        
        // 创建节气日期对象
        const nodeDate = new Date(formData.year, formData.month - 1, nodeDay)
        nodeDate.setHours(0, 0, 0, 0)
        
        // 计算出生日期到节的天数
        const birthTime = birthDate.getTime()
        const nodeTime = nodeDate.getTime()
        const diffDays = (birthTime - nodeTime) / (24 * 60 * 60 * 1000)
        
        console.log('日期计算详情:', {
          出生日期: formatDate(birthDate),
          本月节日期: formatDate(nodeDate),
          相差天数: diffDays.toFixed(2)
        })

        // 计算起运年龄（每3天为1年）
        startingAge = Math.abs(diffDays) / 3
        
        // 计算交运时间（将年龄转换为年月日）
        const yearMs = startingAge * 365 * 24 * 60 * 60 * 1000
        changeYear = new Date(birthDate.getTime() + yearMs)

        // 确保交运时间有效
        if (isNaN(changeYear.getTime())) {
          changeYear = null
          console.error('交运时间计算无效')
        }

        console.log('大运计算结果:', {
          起运年龄: startingAge.toFixed(1),
          交运时间: formatDate(changeYear),
          顺逆: isForward ? '顺行' : '逆行'
        })

        // 更新大运结果
        result.value.destiny = {
          startAge: startingAge.toFixed(1), // 保留一位小数
          changeYear,
          isForward,
          list: [
            // 年柱
            {
              stemBranch: `${lsr.char8.year.stem.name}${lsr.char8.year.branch.name}`,
              takeSound: lsr.char8.year.takeSound?.name || '',
              missing: lsr.char8.year.missing?.join(',') || '',
              element: lsr.char8.year.stem.e5?.name || ''
            },
            // 月柱
            {
              stemBranch: `${lsr.char8.month.stem.name}${lsr.char8.month.branch.name}`,
              takeSound: lsr.char8.month.takeSound?.name || '',
              missing: lsr.char8.month.missing?.join(',') || '',
              element: lsr.char8.month.stem.e5?.name || ''
            },
            // 日柱
            {
              stemBranch: `${lsr.char8.day.stem.name}${lsr.char8.day.branch.name}`,
              takeSound: lsr.char8.day.takeSound?.name || '',
              missing: lsr.char8.day.missing?.join(',') || '',
              element: lsr.char8.day.stem.e5?.name || ''
            },
            // 时柱
            {
              stemBranch: `${lsr.char8.hour.stem.name}${lsr.char8.hour.branch.name}`,
              takeSound: lsr.char8.hour.takeSound?.name || '',
              missing: lsr.char8.hour.missing?.join(',') || '',
              element: lsr.char8.hour.stem.e5?.name || ''
            }
          ]
        }

        // 输出调试信息
        console.log('八字四柱计算结果:', {
          年柱: {
            干支: `${lsr.char8.year.stem.name}${lsr.char8.year.branch.name}`,
            纳音: lsr.char8.year.takeSound?.name,
            五行: lsr.char8.year.stem.e5?.name
          },
          月柱: {
            干支: `${lsr.char8.month.stem.name}${lsr.char8.month.branch.name}`,
            纳音: lsr.char8.month.takeSound?.name,
            五行: lsr.char8.month.stem.e5?.name
          },
          日柱: {
            干支: `${lsr.char8.day.stem.name}${lsr.char8.day.branch.name}`,
            纳音: lsr.char8.day.takeSound?.name,
            五行: lsr.char8.day.stem.e5?.name
          },
          时柱: {
            干支: `${lsr.char8.hour.stem.name}${lsr.char8.hour.branch.name}`,
            纳音: lsr.char8.hour.takeSound?.name,
            五行: lsr.char8.hour.stem.e5?.name
          }
        })
      } else {
        console.error('获取节气信息失败:', {
          年份: formData.year,
          月份: formData.month,
          节气数据: monthTerms
        })
        throw new Error('获取节气信息失败')
      }

      // 生成大运列表
      const destinyList = []
      let currentStem = lsr.char8.month.stem
      let currentBranch = lsr.char8.month.branch

      if (currentStem && currentBranch) {
        // 获取天干和地支的完整序列
        const stems = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸']
        const branches = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥']
        
        // 获取当前干支在序列中的索引
        let stemIndex = stems.indexOf(currentStem.name)
        let branchIndex = branches.indexOf(currentBranch.name)
        
        if (stemIndex !== -1 && branchIndex !== -1) {
          for (let i = 0; i < 8; i++) {
            // 计算大运年龄和年份
            const baseAge = Math.floor(startingAge)
            const age = baseAge + i * 10
            const year = parseInt(formData.year) + age

            // 根据顺序更新索引
            if (isForward) {
              stemIndex = (stemIndex + 1) % 10
              branchIndex = (branchIndex + 1) % 12
            } else {
              stemIndex = (stemIndex - 1 + 10) % 10
              branchIndex = (branchIndex - 1 + 12) % 12
            }

            try {
              // 构建干支字符串
              const stemName = stems[stemIndex]
              const branchName = branches[branchIndex]
              const ganZhi = stemName + branchName

              // 纳音查表（六十甲子纳音，不需要 lunisolar）
              const naYinTable = {
                '甲子': '海中金', '乙丑': '海中金', '丙寅': '炉中火', '丁卯': '炉中火',
                '戊辰': '大林木', '己巳': '大林木', '庚午': '路旁土', '辛未': '路旁土',
                '壬申': '剑锋金', '癸酉': '剑锋金', '甲戌': '山头火', '乙亥': '山头火',
                '丙子': '涧下水', '丁丑': '涧下水', '戊寅': '城头土', '己卯': '城头土',
                '庚辰': '白蜡金', '辛巳': '白蜡金', '壬午': '杨柳木', '癸未': '杨柳木',
                '甲申': '泉中水', '乙酉': '泉中水', '丙戌': '屋上土', '丁亥': '屋上土',
                '戊子': '霹雳火', '己丑': '霹雳火', '庚寅': '松柏木', '辛卯': '松柏木',
                '壬辰': '长流水', '癸巳': '长流水', '甲午': '砂石金', '乙未': '砂石金',
                '丙申': '山下火', '丁酉': '山下火', '戊戌': '平地木', '己亥': '平地木',
                '庚子': '壁上土', '辛丑': '壁上土', '壬寅': '金箔金', '癸卯': '金箔金',
                '甲辰': '覆灯火', '乙巳': '覆灯火', '丙午': '天河水', '丁未': '天河水',
                '戊申': '大驿土', '己酉': '大驿土', '庚戌': '钗钏金', '辛亥': '钗钏金',
                '壬子': '桑柘木', '癸丑': '桑柘木', '甲寅': '大溪水', '乙卯': '大溪水',
                '丙辰': '砂中土', '丁巳': '砂中土', '戊午': '天上火', '己未': '天上火',
                '庚申': '石榴木', '辛酉': '石榴木', '壬戌': '大海水', '癸亥': '大海水'
              }
              const takeSound = naYinTable[ganZhi] || '-'

              // 空亡查表：六十甲子序号 → 旬空
              const ganZhi60 = [];
              for (let k = 0; k < 60; k++) ganZhi60.push(stems[k % 10] + branches[k % 12])
              const xunMissingList = ['戌亥', '申酉', '午未', '辰巳', '寅卯', '子丑']
              const idx60 = ganZhi60.indexOf(ganZhi)
              const missing = (idx60 >= 0) ? xunMissingList[Math.floor(idx60 / 10)] : '-'
              
              destinyList.push({
                index: i + 1,
                age: `${age}`,
                year: `${year}`,
                stem: stemName,
                branch: branchName,
                takeSound,
                missing
              })
            } catch (error) {
              console.warn(`计算第${i + 1}大运信息出错:`, error)
              destinyList.push({
                index: i + 1,
                age: '-',
                year: '-',
                stem: stems[stemIndex] || '-',
                branch: branches[branchIndex] || '-',
                takeSound: '-',
                missing: '-'
              })
            }
          }
        }
      }

      // 更新大运结果
      if (result.value.destiny) {
        result.value.destiny.list = destinyList
      } else {
        result.value.destiny = {
          startAge: startingAge.toFixed(1),
          changeYear,
          isForward,
          list: destinyList
        }
      }

      // 计算流年（纯查表，不依赖 lunisolar 临时对象）
      const calculateFlowYears = () => {
        const currentYear = new Date().getFullYear()
        const flowYears = []
        const tianGanList = ['甲', '乙', '丙', '丁', '戊', '己', '庚', '辛', '壬', '癸']
        const diZhiList = ['子', '丑', '寅', '卯', '辰', '巳', '午', '未', '申', '酉', '戌', '亥']
        const naYinTable = {
          '甲子': '海中金', '乙丑': '海中金', '丙寅': '炉中火', '丁卯': '炉中火',
          '戊辰': '大林木', '己巳': '大林木', '庚午': '路旁土', '辛未': '路旁土',
          '壬申': '剑锋金', '癸酉': '剑锋金', '甲戌': '山头火', '乙亥': '山头火',
          '丙子': '涧下水', '丁丑': '涧下水', '戊寅': '城头土', '己卯': '城头土',
          '庚辰': '白蜡金', '辛巳': '白蜡金', '壬午': '杨柳木', '癸未': '杨柳木',
          '甲申': '泉中水', '乙酉': '泉中水', '丙戌': '屋上土', '丁亥': '屋上土',
          '戊子': '霹雳火', '己丑': '霹雳火', '庚寅': '松柏木', '辛卯': '松柏木',
          '壬辰': '长流水', '癸巳': '长流水', '甲午': '砂石金', '乙未': '砂石金',
          '丙申': '山下火', '丁酉': '山下火', '戊戌': '平地木', '己亥': '平地木',
          '庚子': '壁上土', '辛丑': '壁上土', '壬寅': '金箔金', '癸卯': '金箔金',
          '甲辰': '覆灯火', '乙巳': '覆灯火', '丙午': '天河水', '丁未': '天河水',
          '戊申': '大驿土', '己酉': '大驿土', '庚戌': '钗钏金', '辛亥': '钗钏金',
          '壬子': '桑柘木', '癸丑': '桑柘木', '甲寅': '大溪水', '乙卯': '大溪水',
          '丙辰': '砂中土', '丁巳': '砂中土', '戊午': '天上火', '己未': '天上火',
          '庚申': '石榴木', '辛酉': '石榴木', '壬戌': '大海水', '癸亥': '大海水'
        }
        const xunMissingList = ['戌亥', '申酉', '午未', '辰巳', '寅卯', '子丑']
        
        for (let year = currentYear; year < currentYear + 10; year++) {
          try {
            const age = year - parseInt(formData.year) + 1
            // 年柱干支：天干=(year-4)%10, 地支=(year-4)%12
            const stemName = tianGanList[(year - 4) % 10]
            const branchName = diZhiList[(year - 4) % 12]
            const stemBranch = stemName + branchName
            const takeSound = naYinTable[stemBranch] || stemBranch
            // 空亡查表
            const ganZhi60 = []
            for (let k = 0; k < 60; k++) ganZhi60.push(tianGanList[k % 10] + diZhiList[k % 12])
            const idx60 = ganZhi60.indexOf(stemBranch)
            const missing = (idx60 >= 0) ? xunMissingList[Math.floor(idx60 / 10)] : '-'

            flowYears.push({
              age: `${age}`,
              year: `${year}`,
              stemBranch,
              takeSound,
              missing
            })
          } catch (error) {
            console.warn(`计算${year}年流年出错:`, error)
            continue
          }
        }
        return flowYears
      }

      // 更新流年结果
      result.value.flowYears = calculateFlowYears()

    } catch (error) {
      console.error('计算节气信息出错:', error)
      // 设置默认值
      result.value.solarTerm = {
        current: null,
        prev: null,
        prevQi: null,
        next: null,
        monthTerms: []
      }
      result.value.destiny = {
        startAge: 0,
        changeYear: null,
        isForward: false,
        list: []
      }
      result.value.flowYears = []
    }

    // 在所有计算完成后输出调试信息
    logTermInfo()
  } catch (error) {
    console.error('计算八字出错:', error)
    ElMessage.error('计算八字失败: ' + error.message)
  }
}

// 格式化工具函数
const formatDate = (date) => {
  if (!date) return '-'
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日`
}

const formatGods = (gods) => {
  if (!gods || !gods.length) return '无'
  return gods.map(god => god.name).join('、')
}

const formatDirection = (direction) => {
  if (!direction?.direction) return '无'
  return `${direction.direction}(${direction.description})`
}

const formatTianGanRelations = (relations) => {
  if (!relations) return ''
  const result = []
  for (const pillar in relations) {
    const rel = relations[pillar]
    if (rel && rel.length > 0) {
      result.push(rel.map(r => r.name).join('、'))
    }
  }
  return result.filter(Boolean).join(';')
}

const formatDiZhiRelations = (relations) => {
  if (!relations) return ''
  const result = []
  for (const pillar in relations) {
    const rel = relations[pillar]
    if (rel.triad?.length) result.push(`${pillar}三合[${rel.triad.map(b => b.name).join(',')}]`)
    if (rel.group6) result.push(`${pillar}支六合[${rel.group6.name}]`)
    if (rel.punishing) result.push(`${pillar}支刑[${rel.punishing.name}]`)
    if (rel.conflict) result.push(`${pillar}支冲[${rel.conflict.name}]`)
    if (rel.harming) result.push(`${pillar}支害[${rel.harming.name}]`)
  }
  return result.filter(Boolean).join(';')
}

const formatPercent = (num, total) => {
  if (!num || !total) return '0%'
  return Math.round((num / total) * 100) + '%'
}

// 修改调试信息的输出位置
const logTermInfo = () => {
  if (result.value) {
    console.log('节气信息：', {
      当前节气: result.value.currentTerm?.name,
      上一节: result.value.prevTerm?.name,
      上一气: result.value.prevQi?.name,
      季节: result.value.season,
      当月节气: result.value.monthTerms
    })
  }
}

// 修改 formatGodsVertical 函数
const formatGodsVertical = (gods) => {
  if (!gods || !gods.length) return '无'
  return gods.map(god => {
    const type = god.type === 'good' ? 'success' : 
                god.type === 'bad' ? 'danger' : 
                'info'
    return `<span class="god-name ${type}">${god.name}</span>`
  }).join('\n')
}

// 添加关系类型判断函数
const getRelationType = (type) => {
  switch (type) {
    case 'good':
      return 'success'
    case 'bad':
      return 'danger'
    case 'neutral':
      return 'warning'
    default:
      return 'info'
  }
}

// 修改 formatCangGanVertical 函数
const formatCangGanVertical = (cangGan) => {
  if (!cangGan || !cangGan.length) return '无'
  return cangGan.map(item => {
    // 从藏干名称中提取天干（第一个字）
    const stem = item.name.charAt(0)
    // 获取五行样式类
    const elementClass = getElementClass(stem)
    return `<span class="god-name ${elementClass}">${item.name}</span>`
  }).join('\n')
}

// 添加计算小运的函数
const calculateSmallDestiny = (stemBranch) => {
  if (!stemBranch) return '-'
  // 这里添加小运的计算逻辑
  // 暂时返回干支的最后一个字
  return stemBranch.slice(-1)
}

// 添加五行颜色相关的方法
const getElementClass = (char) => {
  const elementMap = {
    '甲': 'element-wood yang',
    '乙': 'element-wood yin',
    '丙': 'element-fire yang',
    '丁': 'element-fire yin',
    '戊': 'element-earth yang',
    '己': 'element-earth yin',
    '庚': 'element-metal yang',
    '辛': 'element-metal yin',
    '壬': 'element-water yang',
    '癸': 'element-water yin',
    '子': 'element-water yang',
    '丑': 'element-earth yin',
    '寅': 'element-wood yang',
    '卯': 'element-wood yin',
    '辰': 'element-earth yang',
    '巳': 'element-fire yin',
    '午': 'element-fire yang',
    '未': 'element-earth yin',
    '申': 'element-metal yang',
    '酉': 'element-metal yin',
    '戌': 'element-earth yang',
    '亥': 'element-water yin'
  }
  return elementMap[char] || ''
}

const getStemClass = (stemBranch) => {
  const s = typeof stemBranch === 'string' ? stemBranch : ''
  return getElementClass(s.charAt(0))
}

const getBranchClass = (stemBranch) => {
  const s = typeof stemBranch === 'string' ? stemBranch : ''
  return getElementClass(s.charAt(1))
}

// 监听月份变化，自动调整日期
watch([() => formData.year, () => formData.month], () => {
  const maxDays = daysInMonth.value.length
  if (formData.day > maxDays) {
    formData.day = maxDays
  }
}, { immediate: true })

// 添加AI分析相关的状态
const isAnalyzing = ref(false)
const analysisResult = ref('')

// 计算属性：是否有AI分析结果
const hasAIAnalysis = computed(() => {
  return analysisResult.value
})

// AI分析方法
const handleAIAnalysis = async () => {
  if (!requireLogin('bazi-ai')) return
  if (isAnalyzing.value) {
    return
  }

  try {
    console.log('开始八字AI分析...')
    // 积分守卫：余额不足时自动弹出 PayDialog 并中断（与 Tarot/IChing/Dream 同一机制）
    // 动态查价：/api/ai/bazi 实际定价以 feature-cost 接口为准（含新用户首3次免费+会员折扣），
    // 前端不再硬编码 3 灵石；查价失败/非200/异常时跳过前端守卫，由后端扣费权威兜底（真不足会返回 429）
    let price = null // null = 定价未知 → 跳过守卫直接放行
    try {
      const token = localStorage.getItem('token')
      const res = await fetch(`${API_CONFIG.baseUrl}/api/membership/feature-cost?feature=bazi`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      })
      if (res.ok) {
        const data = await res.json()
        const info = (data && typeof data === 'object' && data.data) ? data.data : data
        const costNum = Number(info?.cost)
        if (info && typeof info === 'object' && !Number.isNaN(costNum)) {
          price = { cost: costNum, free: info.free === true }
        }
      } else {
        console.warn(`查询八字AI定价失败（HTTP ${res.status}），跳过前端积分守卫，由后端扣费兜底`)
      }
    } catch (e) {
      console.warn('查询八字AI定价异常，跳过前端积分守卫，由后端扣费兜底:', e)
    }

    if (price && !price.free && price.cost > 0) {
      const canProceed = await checkPoints(price.cost, '八字AI解读')
      if (!canProceed) return
    }
    // price 为 null（查价失败）或 free/cost=0（免费额度）时直接继续，不弹充值窗

    isAnalyzing.value = true
    analysisResult.value = ''

    // 准备分析数据 - 按前端表格顺序
    const r = result.value
    
    // 格式化四柱数据
    const formatFourPillars = () => ({
      四柱: {
        年柱: { 天干: r?.tianGan?.year || '-', 地支: r?.diZhi?.year || '-', 十神: r?.shiShen?.year || '-', 纳音: r?.naYin?.year || '-', 空亡: r?.kongWang?.year || '-', 长生: r?.lifeGods?.year?.name || '-' },
        月柱: { 天干: r?.tianGan?.month || '-', 地支: r?.diZhi?.month || '-', 十神: r?.shiShen?.month || '-', 纳音: r?.naYin?.month || '-', 空亡: r?.kongWang?.month || '-', 长生: r?.lifeGods?.month?.name || '-' },
        日柱: { 天干: r?.tianGan?.day || '-', 地支: r?.diZhi?.day || '-', 十神: r?.shiShen?.day || '-', 纳音: r?.naYin?.day || '-', 空亡: r?.kongWang?.day || '-', 长生: r?.lifeGods?.day?.name || '-' },
        时柱: { 天干: r?.tianGan?.hour || '-', 地支: r?.diZhi?.hour || '-', 十神: r?.shiShen?.hour || '-', 纳音: r?.naYin?.hour || '-', 空亡: r?.kongWang?.hour || '-', 长生: r?.lifeGods?.hour?.name || '-' }
      }
    })

    // 藏干信息
    const formatCangGan = () => ({
      年柱藏干: r?.cangGan?.year?.map(item => item.name).join('、') || '-',
      月柱藏干: r?.cangGan?.month?.map(item => item.name).join('、') || '-',
      日柱藏干: r?.cangGan?.day?.map(item => item.name).join('、') || '-',
      时柱藏干: r?.cangGan?.hour?.map(item => item.name).join('、') || '-'
    })

    // 阴阳五行统计
    const formatWuxing = () => ({
      日主: `${r?.dayMaster?.element || '-'}(${r?.dayMaster?.strength > 0 ? '旺' : '弱'}, 得分:${r?.dayMaster?.strength || 0})`,
      八字五行: `金:${r?.elements?.['金'] || 0}、木:${r?.elements?.['木'] || 0}、水:${r?.elements?.['水'] || 0}、火:${r?.elements?.['火'] || 0}、土:${r?.elements?.['土'] || 0}`,
      藏干五行: `金:${r?.hiddenElements?.['金'] || 0}、木:${r?.hiddenElements?.['木'] || 0}、水:${r?.hiddenElements?.['水'] || 0}、火:${r?.hiddenElements?.['火'] || 0}、土:${r?.hiddenElements?.['土'] || 0}`,
      八字阴阳: `阴:${r?.yinYangCount?.yin || 0}、阳:${r?.yinYangCount?.yang || 0}`,
      藏干阴阳: `阴:${r?.hiddenYinYangCount?.yin || 0}、阳:${r?.hiddenYinYangCount?.yang || 0}`
    })

    // 大运流年
    const formatDestiny = () => ({
      起运时间: `${r?.destiny?.startAge || '-'}岁（${r?.destiny?.changeYear || '-'}）`,
      运势方向: r?.destiny?.isForward ? '顺行' : '逆行',
      大运: r?.destiny?.list?.slice(0, 8)?.map(item => `${item.stemBranch || (item.stem + item.branch)}（${item.takeSound || '-'}/${item.missing || '-'}）`).join('、') || '-',
      流年: r?.flowYears?.slice(0, 10)?.map(item => `${item.age}岁:${item.stemBranch}`).join('、') || '-'
    })

    const analysisData = {
      // 基本信息
      性别: formData.gender || '男',
      公历出生: formatDate(new Date(formData.year, formData.month - 1, formData.day)),
      农历出生: r?.lunarDate || '-',
      生肖: r?.zodiac || '-',
      季节: r?.season || '-',
      
      // 节气
      当日节气: r?.solarTerm?.current?.name || '-',
      本月节名: r?.solarTerm?.monthTermName || '-',
      本月气名: r?.solarTerm?.monthQiName || '-',
      本月节气: r?.solarTerm?.monthTerms?.join('、') || '-',
      
      // 四柱
      ...formatFourPillars(),
      
      // 藏干
      ...formatCangGan(),
      
      // 阴阳五行
      ...formatWuxing(),
      
      // 大运流年
      ...formatDestiny()
    }

    console.log('分析数据:', JSON.stringify(analysisData, null, 2))

    try {
      // 调用AI分析服务
      const content = await analyzeBazi(analysisData)

      if (!content) {
        throw new Error('未能获取到有效的分析结果')
      }

      // 确保最终结果被设置
      analysisResult.value = content
      console.log('八字分析完成，最终内容:', content)
      // 注：交互埋点由服务端 ai_service.py 在 AI 返回后统一写入，前端不再重复记录
    } catch (error) {
      console.error('AI分析失败:', error)
      throw new Error('AI分析失败: ' + (error.message || '未知错误'))
    }
  } catch (error) {
    console.error('八字分析失败:', error)
    ElMessage.error(error.message || '分析失败，请稍后重试')
    analysisResult.value = ''
  } finally {
    isAnalyzing.value = false
  }
}

// 修改格式化分析结果的计算属性
const formattedAnalysis = computed(() => {
  if (!analysisResult.value) return ''
  
  try {
    // 直接使用 marked 渲染，不做破坏性预处理
    // gfm: true 已启用，支持表格
    return marked(analysisResult.value)
  } catch (error) {
    console.error('Markdown 渲染错误:', error)
    return `<pre>${analysisResult.value}</pre>`
  }
})

defineExpose({
  baziResult: result
})

</script>

<style lang="scss">
/* Mixins */
@mixin element-style($color) {
  color: $color;
  text-shadow: 0 0 8px rgba($color, 0.6);
}

@mixin god-tag($color) {
  background: rgba($color, 0.1);
  color: $color;
}

/* Container (.bazi-view) removed — PageLayout slot provides layout */

@keyframes stars {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes twinkle {
  0% { opacity: 0.3; }
  100% { opacity: 0.8; }
}

/* 修改卡片样式 */
.input-section,
.info-card,
.bazi-table,
.destiny-grid {
  background: var(--fh-card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--color-accent);
  box-shadow: 0 8px 32px var(--fh-shadow-medium);
  transition: all var(--transition-normal);
  position: relative;
  z-index: 2;

  &:hover {
    background: var(--fh-surface-elevated);
    transform: translateY(-2px);
    box-shadow: 0 12px 48px var(--fh-overlay);
  }
}

/* 修改表格样式 */
.bazi-table {
  th {
    background: rgba(var(--color-accent-rgb), 0.1);
    border-bottom: 1px solid var(--color-accent);
  }

  td {
    border: 1px solid rgba(var(--color-accent-rgb), 0.2);
  }

  tr:hover td {
    background: rgba(var(--color-accent-rgb), 0.1);
  }
}

/* 修改网格样式 */
.destiny-grid {
  .grid-row {
    border-bottom: 1px solid rgba(var(--color-accent-rgb), 0.2);

    .grid-cell {
      border-right: 1px solid rgba(var(--color-accent-rgb), 0.2);

      &.label {
        background: rgba(var(--color-accent-rgb), 0.1);
      }
    }
  }
}

/* 修改信息项样式 */
.info-item {
  background: rgba(var(--color-accent-rgb), 0.1) !important;
  border: 1px solid var(--color-accent) !important;
}

/* 输入区域 */
.input-section {
  background: var(--fh-card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--fh-border);
  border-radius: var(--radius-lg);
  padding: var(--spacing-xl);
  margin-bottom: var(--spacing-xl);

  :deep(.el-select) {
    .el-input__wrapper {
      background: var(--fh-white-overlay);
      box-shadow: none;
      border: none;
      
      &:hover {
        background: var(--fh-decoration-1);
      }
      
      &.is-focus {
        background: var(--fh-decoration-2);
        box-shadow: 0 0 0 1px var(--color-accent);
      }
    }
    
    .el-input__inner {
      color: var(--color-text-light);
      font-size: 1rem;
      height: 36px;
    }
  }

  .gender-select {
    display: flex;
    justify-content: center;
    margin-top: var(--spacing-md);

    :deep(.el-radio-group) {
      display: flex;
      gap: var(--spacing-xl);

      .el-radio {
        margin-right: 0;
        padding: var(--spacing-sm) var(--spacing-lg);
        border-radius: var(--radius-md);
        transition: all var(--transition-normal);

        &:hover {
          background: var(--fh-decoration-2);
        }

        .el-radio__label {
          color: var(--color-light);
          font-size: 1.1rem;
        }

        &.is-checked .el-radio__label {
          color: var(--color-accent);
        }
      }
    }
  }
}

:deep(.el-select-dropdown__item) {
  color: var(--color-text-light);
  
  &.selected {
    color: var(--color-accent);
    font-weight: bold;
  }
  
  &:hover {
    background: var(--fh-white-overlay);
  }
}

/* 结果区域 */
.result-section {
  .info-card {
    background: var(--fh-card-bg);
    backdrop-filter: blur(10px);
    border: 1px solid var(--fh-border);
    border-radius: var(--radius-lg);
    padding: var(--spacing-lg);
    margin-bottom: var(--spacing-lg);
    transition: transform var(--transition-normal);

    &:hover {
      transform: translateY(-2px);
      box-shadow: var(--shadow-hover);
    }

    .card-header {
      color: var(--color-accent);
      text-align: center;
      margin-bottom: var(--spacing-lg);
      font-size: 1.2rem;
      font-family: var(--font-mystical);
    }
  }
}

/* 八字表格 */
.bazi-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  margin: var(--spacing-lg) 0;
  background: var(--fh-card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--fh-border);
  border-radius: var(--radius-lg);
  overflow: hidden;

  th {
    background: var(--fh-decoration-1);
    color: var(--color-accent);
    padding: var(--spacing-lg);
    text-align: center;
    font-size: 1.1rem;
    letter-spacing: 2px;
    font-family: var(--font-mystical);
  }

  td {
    padding: var(--spacing-lg);
    border: 1px solid var(--fh-border);
    text-align: center;
    font-size: 1rem;
    line-height: 1.6;
    color: var(--color-light);
    transition: background-color var(--transition-normal);
  }

  tr:hover td {
    background: var(--fh-decoration-2);
  }
}

/* 五行元素样式 */
.element {
  &-wood {
    &.yang { @include element-style(#2E7D32); }
    &.yin { @include element-style(#66BB6A); }
  }

  &-fire {
    &.yang { @include element-style(#D84315); }
    &.yin { @include element-style(#FF7043); }
  }

  &-earth {
    &.yang { @include element-style(#F9A825); }
    &.yin { @include element-style(#FFD54F); }
  }

  &-metal {
    &.yang { @include element-style(#CFD8DC); }
    &.yin { @include element-style(#ECEFF1); }
  }

  &-water {
    &.yang { @include element-style(#1565C0); }
    &.yin { @include element-style(#42A5F5); }
  }
}

/* 神煞标签 */
.god-name {
  display: inline-block;
  padding: var(--spacing-xs) var(--spacing-sm);
  margin: var(--spacing-xs);
  border-radius: var(--radius-sm);
  font-size: 0.9em;
  
  &.success {
    @include god-tag(var(--color-success));
  }
  
  &.danger {
    @include god-tag(var(--color-danger));
  }
  
  &.info {
    @include god-tag(var(--color-info));
  }
}

/* 垂直列表 */
.vertical-list {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
  min-height: 40px;
  padding: var(--spacing-xs);
}

/* 大运流年网格 */
.destiny-grid {
  background: var(--fh-card-bg);
  backdrop-filter: blur(10px);
  border: 1px solid var(--fh-border);
  border-radius: var(--radius-lg);
  margin-top: var(--spacing-lg);
  overflow: hidden;

  .grid-row {
    display: flex;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    &:last-child {
      border-bottom: none;
    }

    .grid-cell {
      flex: 1;
      padding: var(--spacing-sm);
      text-align: center;
      border-right: 1px solid rgba(255, 255, 255, 0.1);
      min-width: 80px;
      color: var(--color-light);

      &:last-child {
        border-right: none;
      }

      &.label {
        background: var(--fh-decoration-1);
        color: var(--color-accent);
        min-width: 60px;
        flex: 0 0 60px;
        font-family: var(--font-mystical);
      }
    }
  }
}

/* 阴阳五行信息 */
.yinyang-wuxing-info {
  padding: var(--spacing-lg);

  .info-group {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-lg);

    .info-row {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: var(--spacing-lg);

      .info-item {
        padding: var(--spacing-md);
        background: var(--fh-decoration-2);
        border-radius: var(--radius-lg);
        border: 1px solid var(--fh-border);
        color: var(--color-light);

        &.full-width {
          grid-column: 1 / -1;
        }

        .label {
          color: var(--color-accent);
          font-weight: 600;
          margin-right: var(--spacing-sm);
          display: inline-block;
          min-width: 80px;
        }
      }
    }
  }
}

/* 动画 */
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

/* 响应式布局 */
@media (max-width: 768px) {
  .bazi-table {
    display: block;
    max-width: 100%;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    font-size: 0.9rem;

    th {
      padding: 8px 4px;
      font-size: 0.8rem;
    }

    td {
      padding: 8px 4px;
      font-size: 0.8rem;
    }
  }

  .info-card {
    .card-header {
      padding: var(--spacing-sm) var(--spacing-md);
    }
  }

  .yinyang-wuxing-info {
    .info-row {
      grid-template-columns: 1fr !important;
    }
  }
}

/* 超小屏幕适配 */
@media (max-width: 400px) {
  .input-section {
    padding: var(--spacing-md);
  }

  .bazi-table {
    th,
    td {
      padding: 6px 2px;
      font-size: 0.75rem;
    }
  }

  .info-card {
    padding: var(--spacing-md) !important;
  }

  .yinyang-wuxing-info .label {
    min-width: auto;
  }

  .basic-info {
    grid-template-columns: 1fr;
  }

  .destiny-grid .grid-cell {
    min-width: auto;
  }

  .destiny-grid .grid-cell.label {
    min-width: auto;
    flex: 0 0 auto;
  }
}

/* 打印样式 */
@media print {
  .info-card {
    break-inside: avoid;
    box-shadow: none;
    border: 1px solid #ccc;
  }

  .bazi-table {
    border: 1px solid #ccc;

    th {
      background: #f5f5f5;
      color: #000;
    }
  }
}

.basic-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
  color: var(--color-text-light);
}

.info-group {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  border-radius: var(--radius-lg);
  background: var(--fh-white-overlay-soft);
  backdrop-filter: blur(10px);
}

.info-group h3 {
  color: var(--color-accent);
  font-size: 1.2rem;
  margin-bottom: var(--spacing-sm);
}

.info-group p {
  margin: 0;
  line-height: 1.6;
}

.info-item {
  color: var(--color-text-light);
  font-size: 1rem;
  padding: var(--spacing-sm);
  background: var(--fh-white-overlay-soft);
  border-radius: var(--radius-sm);
  backdrop-filter: blur(5px);
  transition: all var(--transition-normal);
}

.info-item:hover {
  background: var(--fh-white-overlay);
}

/* 调整卡片透明度 */
:deep(.mystical-card) {
  background: var(--fh-card-bg) !important;
}

/* 其他样式保持不变 */

/* 添加AI分析区域样式 */
.ai-analysis-section {
  margin-top: var(--spacing-xl);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xl);

  .analyze-button {
    min-width: 150px;
    padding: var(--spacing-md) var(--spacing-xl);
    
    .icon {
      font-size: 1.2em;
      margin-right: var(--spacing-sm);
    }
  }

  .analysis-result {
    width: 100%;
    max-width: 800px;
    margin: 0 auto;

    .result-card {
      background: var(--fh-card-bg);
      backdrop-filter: blur(10px);
      border: 1px solid var(--color-accent);
      
      .result-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        padding: var(--spacing-lg);
        border-bottom: 1px solid rgba(var(--color-accent-rgb), 0.2);

        .header-icon {
          font-size: 1.8em;
          animation: float 3s ease-in-out infinite;
        }

        .header-text {
          flex: 1;
          
          h3 {
            font-size: 1.4em;
            margin: 0;
            color: var(--color-accent);
            font-family: var(--font-mystical);
          }

          p {
            margin: var(--spacing-xs) 0 0;
            color: var(--color-text-light);
            opacity: 0.8;
          }
        }

        .header-status {
          padding: var(--spacing-sm) var(--spacing-md);
          border-radius: var(--radius-full);
          background: rgba(var(--color-success-rgb), 0.1);
          color: var(--color-success);
          font-size: 0.9em;

          &.analyzing {
            background: rgba(var(--color-warning-rgb), 0.1);
            color: var(--color-warning);
            animation: pulse 1.5s ease-in-out infinite;
          }
        }
      }

      .result-content {
        padding: var(--spacing-xl);
        min-height: 200px;

        &.loading {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: var(--spacing-lg);
        }

        .loading-animation {
          text-align: center;

          .crystal-ball {
            font-size: 3em;
            animation: crystalFloat 2s ease-in-out infinite;
          }

          p {
            margin-top: var(--spacing-md);
            color: var(--color-text-light);
            opacity: 0.8;
          }
        }

        .analysis-text {
          line-height: 1.8;
          color: var(--color-text-light);
          white-space: pre-wrap;

          h4 {
            color: var(--color-accent);
            margin: var(--spacing-lg) 0 var(--spacing-md);
            font-size: 1.2em;
            border-bottom: 1px solid rgba(var(--color-accent-rgb), 0.2);
            padding-bottom: var(--spacing-sm);
          }

          p {
            margin: var(--spacing-md) 0;
            text-indent: 2em;
          }
        }
      }
    }
  }
}

/* 动画 */
@keyframes float {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}

@keyframes crystalFloat {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
    filter: brightness(1);
  }
  50% { 
    transform: translateY(-10px) rotate(10deg);
    filter: brightness(1.2);
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

/* 响应式调整 */
@media (max-width: 768px) {
  .ai-analysis-section {
    .analysis-result {
      .result-card {
        .result-header {
          flex-direction: column;
          text-align: center;
          
          .header-text {
            h3 { font-size: 1.2em; }
          }
        }

        .result-content {
          padding: var(--spacing-md);
        }
      }
    }
  }
}

/* AI分析结果的Markdown样式 */
.analysis-text {
  line-height: 1.8;
  color: var(--color-text-light);

  .analysis-h1,
  .analysis-h2,
  .analysis-h3,
  .analysis-h4 {
    color: var(--color-accent);
    font-family: var(--font-mystical);
    margin: var(--spacing-lg) 0 var(--spacing-md);
    padding-bottom: var(--spacing-sm);
    border-bottom: 1px solid rgba(var(--color-accent-rgb), 0.2);
  }

  .analysis-h1 { font-size: 1.8em; }
  .analysis-h2 { font-size: 1.6em; }
  .analysis-h3 { font-size: 1.4em; }
  .analysis-h4 { font-size: 1.2em; }

  .analysis-p {
    margin: var(--spacing-md) 0;
    text-indent: 2em;
    line-height: 1.8;
  }

  .analysis-ul {
    margin: var(--spacing-md) 0;
    padding-left: 2em;
  }

  .analysis-li {
    margin: var(--spacing-sm) 0;
    line-height: 1.6;
    
    &::marker {
      color: var(--color-accent);
    }
  }

  code {
    background: rgba(var(--color-accent-rgb), 0.1);
    padding: 0.2em 0.4em;
    border-radius: var(--radius-sm);
    font-family: var(--font-mono);
    font-size: 0.9em;
  }

  blockquote {
    margin: var(--spacing-md) 0;
    padding: var(--spacing-md) var(--spacing-lg);
    border-left: 4px solid var(--color-accent);
    background: rgba(var(--color-accent-rgb), 0.1);
    font-style: italic;
  }

  strong {
    color: var(--color-accent);
    font-weight: 600;
  }

  em {
    color: var(--color-warning);
    font-style: italic;
  }
}

/* 添加打字机效果 */
@keyframes typing {
  from { width: 0 }
  to { width: 100% }
}

.typing-effect {
  overflow: hidden;
  white-space: nowrap;
  animation: typing 0.5s steps(40, end);
}

/* 添加 Markdown 样式 */
.analysis-text {
  :deep(h1), :deep(h2), :deep(h3), :deep(h4) {
    color: var(--color-accent);
    font-family: var(--font-mystical);
    margin: var(--spacing-lg) 0 var(--spacing-md);
    padding-bottom: var(--spacing-sm);
    border-bottom: 1px solid rgba(var(--color-accent-rgb), 0.2);
  }

  :deep(h1) { font-size: 1.8em; }
  :deep(h2) { font-size: 1.6em; }
  :deep(h3) { font-size: 1.4em; }
  :deep(h4) { font-size: 1.2em; }

  :deep(p) {
    margin: var(--spacing-md) 0;
    text-indent: 2em;
    line-height: 1.8;
  }

  :deep(ul) {
    margin: var(--spacing-md) 0;
    padding-left: 2em;
  }

  :deep(li) {
    margin: var(--spacing-sm) 0;
    line-height: 1.6;
    
    &::marker {
      color: var(--color-accent);
    }
  }

  :deep(code) {
    background: rgba(var(--color-accent-rgb), 0.1);
    padding: 0.2em 0.4em;
    border-radius: var(--radius-sm);
    font-family: var(--font-mono);
    font-size: 0.9em;
  }

  :deep(blockquote) {
    margin: var(--spacing-md) 0;
    padding: var(--spacing-md) var(--spacing-lg);
    border-left: 4px solid var(--color-accent);
    background: rgba(var(--color-accent-rgb), 0.1);
    font-style: italic;
  }

  :deep(strong) {
    color: var(--color-accent);
    font-weight: 600;
  }

  :deep(em) {
    color: var(--color-warning);
    font-style: italic;
  }
}

/* ==================== 移动端独立样式 ==================== */

/* 输入卡片 */
.mobile-input-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: var(--mobile-radius-lg);
  padding: var(--mobile-spacing-lg);
  margin-bottom: var(--mobile-spacing-lg);
}

.mobile-section-title {
  font-size: var(--mobile-font-md);
  font-weight: 700;
  color: #333;
  margin-bottom: var(--mobile-spacing-md);
}

.mobile-section-subtitle {
  font-size: var(--mobile-font-sm);
  font-weight: 600;
  color: #666;
  margin: var(--mobile-spacing-md) 0 var(--mobile-spacing-sm);
}

/* 日期选择 2×2 网格 */
.mobile-date-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--mobile-spacing-sm);
  margin-bottom: var(--mobile-spacing-md);
}

.mobile-date-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.mobile-date-item .el-select {
  flex: 1;
  min-width: 0;
}

.mobile-date-unit {
  font-size: var(--mobile-font-sm);
  color: #999;
  flex-shrink: 0;
}

/* 性别 segmented control */
.mobile-gender-row {
  display: flex;
  border-radius: var(--mobile-radius-md);
  overflow: hidden;
  border: 1px solid #e0e0e0;
}

.mobile-gender-btn {
  flex: 1;
  padding: 10px 0;
  border: none;
  background: #f7f7f7;
  font-size: var(--mobile-font-base);
  color: #666;
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 44px;

  &:first-child {
    border-right: 1px solid #e0e0e0;
  }

  &.active {
    background: #e8f5e9;
    color: #2e7d32;
    font-weight: 700;
  }

  &:active {
    opacity: 0.8;
  }
}

/* 通用卡片 */
.mobile-card {
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: var(--mobile-radius-lg);
  padding: var(--mobile-spacing-lg);
  margin-bottom: var(--mobile-spacing-lg);
}

/* 基本信息网格 */
.mobile-info-grid {
  display: flex;
  flex-wrap: wrap;
  gap: var(--mobile-spacing-sm);
}

.mobile-info-item {
  flex: 1 1 calc(50% - var(--mobile-spacing-sm));
  min-width: 0;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: var(--mobile-spacing-sm) var(--mobile-spacing-md);
  background: #f9f9f9;
  border-radius: var(--mobile-radius-sm);
  font-size: var(--mobile-font-sm);
  gap: var(--mobile-spacing-xs);
}

.mobile-info-label {
  color: #999;
  flex-shrink: 0;
}

.mobile-info-value {
  color: #333;
  font-weight: 500;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* 四柱命盘 */
.mobile-pillar-list {
  display: flex;
  flex-direction: column;
  gap: var(--mobile-spacing-md);
}

.mobile-pillar-card {
  background: #fafafa;
  border: 1px solid #eee;
  border-radius: var(--mobile-radius-md);
  padding: var(--mobile-spacing-md);
}

.mobile-pillar-header {
  font-size: var(--mobile-font-sm);
  color: #999;
  font-weight: 500;
  margin-bottom: var(--mobile-spacing-sm);
}

.mobile-pillar-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--mobile-spacing-xl);
  margin-bottom: var(--mobile-spacing-md);
}

.mobile-pillar-gan,
.mobile-pillar-zhi {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  flex: 1;
}

.mobile-pillar-char {
  font-size: var(--mobile-font-2xl);
  font-weight: 700;
  line-height: 1.3;
}

.mobile-pillar-attr {
  font-size: var(--mobile-font-xs);
  color: #999;
}

.mobile-pillar-divider {
  width: 1px;
  height: 36px;
  background: #e0e0e0;
  flex-shrink: 0;
}

.mobile-pillar-meta {
  display: flex;
  flex-wrap: wrap;
  gap: var(--mobile-spacing-xs);
  margin-top: var(--mobile-spacing-xs);
}

.mobile-pillar-tag {
  font-size: var(--mobile-font-xs);
  color: #666;
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 4px;
}

.mobile-pillar-relations {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: var(--mobile-spacing-xs);
}

.mobile-relation-tag {
  transform: scale(0.9);
  transform-origin: left center;
}

/* 五行进度条 */
.mobile-wuxing-section {
  padding: var(--mobile-spacing-sm) 0;
}

.mobile-daymaster {
  font-size: var(--mobile-font-sm);
  color: #333;
  margin-bottom: var(--mobile-spacing-md);
  font-weight: 500;
}

.mobile-wuxing-row {
  display: flex;
  align-items: center;
  gap: var(--mobile-spacing-sm);
  margin-bottom: var(--mobile-spacing-sm);
}

.mobile-wuxing-label {
  width: 20px;
  font-size: var(--mobile-font-sm);
  font-weight: 700;
  text-align: center;
  flex-shrink: 0;
}

.mobile-wuxing-bar-bg {
  flex: 1;
  height: 8px;
  background: #f0f0f0;
  border-radius: 4px;
  overflow: hidden;
}

.mobile-wuxing-bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
  min-width: 4px;
}

.mobile-wuxing-count {
  width: 20px;
  font-size: var(--mobile-font-sm);
  color: #666;
  font-weight: 600;
  text-align: right;
  flex-shrink: 0;
}

/* 大运流年 */
.mobile-destiny-header {
  display: flex;
  justify-content: space-between;
  font-size: var(--mobile-font-sm);
  color: #666;
  margin-bottom: var(--mobile-spacing-md);
  padding-bottom: var(--mobile-spacing-sm);
  border-bottom: 1px solid #f0f0f0;
}

.mobile-destiny-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin: 0 calc(-1 * var(--mobile-spacing-lg));
  padding: 0 var(--mobile-spacing-lg);
}

.mobile-destiny-grid {
  display: flex;
  gap: var(--mobile-spacing-sm);
  min-width: max-content;
}

.mobile-destiny-item {
  flex-shrink: 0;
  width: 64px;
  text-align: center;
  padding: var(--mobile-spacing-sm) var(--mobile-spacing-xs);
  background: #f9f9f9;
  border-radius: var(--mobile-radius-sm);
  border: 1px solid #f0f0f0;
}

.mobile-destiny-age {
  font-size: var(--mobile-font-xs);
  color: #999;
  margin-bottom: 2px;
}

.mobile-destiny-gz {
  font-size: var(--mobile-font-base);
  font-weight: 700;
  line-height: 1.4;
}

.mobile-destiny-sound {
  font-size: 10px;
  color: #bbb;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* AI 解答 */
.mobile-ai-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--mobile-spacing-lg);
  margin-bottom: var(--mobile-spacing-2xl);
  padding-bottom: var(--mobile-bottom-padding, 60px);
}

.mobile-ai-btn {
  width: 100%;
  padding: 12px 0;
  border: none;
  border-radius: var(--mobile-radius-md);
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  font-size: var(--mobile-font-base);
  font-weight: 600;
  cursor: pointer;
  min-height: 44px;
  transition: opacity 0.2s;

  &:active {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.mobile-ai-result {
  width: 100%;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: var(--mobile-radius-lg);
  padding: var(--mobile-spacing-lg);
  font-size: var(--mobile-font-sm);
  color: #333;
  line-height: var(--mobile-line-height);

  :deep(h3), :deep(h4) {
    color: #333;
    margin: var(--mobile-spacing-md) 0 var(--mobile-spacing-sm);
    font-size: var(--mobile-font-md);
  }

  :deep(p) {
    margin: var(--mobile-spacing-sm) 0;
    text-indent: 2em;
  }

  :deep(strong) {
    color: #333;
  }
}
</style>
